import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import LightSelect from "../../CompanyView/LightSelect";

import { WIDGETS, widgetById, isEditable, defaultGraphFor, GRAPH_OPTIONS, layoutsFor, MAX_METRICS, DEFAULT_LAYOUT, conformLayout, METRIC_COLORS, NO_COLOR, COMPANY_COLOR, GOAL_COLOR, SHOW_OPTIONS, customWidthFor, baseId } from "./widgetRegistry";
import { EarningsSetup, LadderSetup } from "./MoneySetup";
import LeaderboardSetup, { WorkspacePicker, useLiveColumns } from "./LeaderboardSetup";
import AnnualCompSetup from "./AnnualCompSetup";
import LaborSetup from "./LaborSetup";
import ProfitTrendSetup from "./ProfitTrendSetup";
import DeptMetricsSetup, { DeptTileStyle } from "./DeptMetricsSetup";
import ScopeWindow from "./ScopeWindow";
import YetiOptionsField from "./YetiOptionsField";
import RphGoalField from "./RphGoalField";
import { hasRphMetric } from "./rphGoal";
import BonusField from "./BonusField";
import { BEAT_YETI_RATE_ID, usesMetricPairing } from "../../../helpers/derivedMetricIds";
import MetricPlanEditor from "./MetricPlanEditor";
import { TEMPLATES } from "./templates";
import { hasEarningsRules, hasLadder } from "../../../components/SalesBonus/moneyRules";
import { sanitizeBonus, isStepBonus, moneyLabel } from "../../../components/CustomWidget/bonusSteps";
import { defaultPayoutCurve, defaultPieceLayout, defaultGate, unitForMetricLabel, PAYOUT_UNITS, GATE_SOURCES, GATE_PERIODS, GATE_MODES, gatePeriodKey, gatePeriodLabel, gateMin } from "../../../components/CustomWidget/payoutCurve";
import { UseSelectedWorkspace, UseCompanyWorkspaces, UseCompanyColors } from "../../../hooks/company";
import { hasMetricPlan } from "../../../helpers/metricPlan";
import { MetricPicker, metricsForWorkspaces, metricGroupLabel, metricWorkspaceTag } from "./MetricPicker";
import "./customize.css";

/**
 * The component library, and the editor for one component.
 *
 * Both live in the same docked panel, as two views of it — the board is pushed
 * aside, never covered, because you have to see where a piece lands and what it
 * looks like once it has.
 *
 * Decisions worth knowing:
 *
 * 1. THE PREVIEW GETS THE FULL WIDTH, above the text. It used to be a fixed
 *    152×86 box beside the copy, which left no room for a name or a number and
 *    made every preview read as a sketch. Stacked, it is ~336×150 — enough for
 *    the real component (see ./widgetPreviews).
 * 2. TWO ACTIONS PER ROW. `+` puts it on the board now. The gear opens it in
 *    this same panel to set up FIRST — nothing touches the board until you
 *    confirm, and a dashed card shows where it will land.
 * 3. TEMPLATES IS NOT A FILTER. It used to be a chip in the filter row, between
 *    "Sales 2" and "People 3", which is why nobody could find it. It is a
 *    whole-board action and gets its own labelled row.
 * 4. Nothing is greyed out. The list is only what you can still add; what is
 *    already placed appears at the bottom as tags that edit and remove.
 */

const ORDER = ["Performance", "Sales", "People", "Essentials", "Your own"];

const Icon = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const IcPlus = <path d="M12 5v14M5 12h14" />;
const IcX = <path d="M18 6L6 18M6 6l12 12" />;
const IcBack = <><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></>;
const IcGear = (
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </>
);
const IcGrid = (
  <>
    <rect x="3" y="3" width="7" height="9" rx="1.4" />
    <rect x="14" y="3" width="7" height="5" rx="1.4" />
    <rect x="14" y="12" width="7" height="9" rx="1.4" />
    <rect x="3" y="16" width="7" height="5" rx="1.4" />
  </>
);

const IcCheck = <path d="M20 6 9 17l-5-5" />;
const IcSwap = <><path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="M16 21l4-4-4-4" /><path d="M20 17H4" /></>;

const emptyMessage = (q, query) =>
  q ? `No components match “${query}”.` : "No components in this category.";

/* ── shared "Build your own" pickers (used by both create and edit) ──────── */

const MiniGraph = ({ kind }) => {
  // Tiny glyph beside each graph choice so the option reads at a glance.
  if (kind === "ring") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
        <circle cx="12" cy="12" r="8" opacity="0.25" />
        <path d="M12 4a8 8 0 0 1 7 4" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "donut") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
        <circle cx="12" cy="12" r="7" opacity="0.25" />
        <path d="M12 5a7 7 0 0 1 6 3.6" strokeLinecap="butt" />
      </svg>
    );
  }
  if (kind === "gauge") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M4 15a8 8 0 0 1 16 0" opacity="0.25" />
        <path d="M4 15a8 8 0 0 1 5-7.4" />
      </svg>
    );
  }
  if (kind === "target") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M4 12h11" /><path d="M4 12h16" opacity="0.25" />
      </svg>
    );
  }
  if (kind === "bar") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M5 20V4" opacity="0.25" /><path d="M7 20h3M13 20h3M19 20h1" opacity="0" />
        <rect x="5" y="12" width="3" height="8" rx="0.6" fill="currentColor" stroke="none" />
        <rect x="10.5" y="8" width="3" height="12" rx="0.6" fill="currentColor" stroke="none" />
        <rect x="16" y="14" width="3" height="6" rx="0.6" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (kind === "line") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15l4-5 4 3 4-6 4 4" />
      </svg>
    );
  }
  if (kind === "area") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15l4-5 4 3 4-6 4 4v6H4z" fill="currentColor" fillOpacity="0.18" />
      </svg>
    );
  }
  if (kind === "spark") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 14l3-3 3 2 3-5 3 4 3-2 3 3" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 16V9M12 16V6M18 16v-4" opacity="0.25" /><path d="M5 19h14" />
    </svg>
  );
};

/* The graph choice, as PICTURES of the graphs.
   A dropdown made you read nine words to find a shape; the shapes are the whole
   point. So the four that cover most cards show as icons, and "More" opens the
   rest in place. If the chosen graph is one of the extra five, the picker opens
   already expanded — the current choice must always be on screen. */
const COMMON_GRAPHS = ["number", "target", "line", "bar"];

/* A metric's colour — one swatch drives its target bar, its sparkline and its
   ring, so a metric is a single visual decision rather than three. Lives here in
   Visualisation next to the graph, because that is the question it answers:
   how does this look. Setup answers what the card is made of. */
const ColourPicker = ({ value, fallback, onChange }) => {
  const { primaryColor } = UseCompanyColors() || {};
  const companyColor = primaryColor || "#1B598E";
  const isSentinel = value === NO_COLOR || value === COMPANY_COLOR || value === GOAL_COLOR;
  return (
    <div className="gmbw-swatches" role="group" aria-label="Metric colour">
      {METRIC_COLORS.map((c) => {
        const on = !isSentinel && (value || fallback) === c;
        return (
          <button
            key={c}
            type="button"
            className={`gmbw-swatch${on ? " on" : ""}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
            aria-label={`Use ${c}`}
            aria-pressed={on}
          />
        );
      })}
      {/* Company colour — tracks the tenant's brand primary (not a fixed hex). */}
      <button
        type="button"
        className={`gmbw-swatch gmbw-swatch-company${value === COMPANY_COLOR ? " on" : ""}`}
        style={{ background: companyColor }}
        onClick={() => onChange(COMPANY_COLOR)}
        aria-label="Company colour"
        aria-pressed={value === COMPANY_COLOR}
        title="Company colour"
      />
      {/* By goal — green when the metric hits its target, red when it hasn't. */}
      <button
        type="button"
        className={`gmbw-swatch gmbw-swatch-goal${value === GOAL_COLOR ? " on" : ""}`}
        onClick={() => onChange(GOAL_COLOR)}
        aria-label="By goal"
        aria-pressed={value === GOAL_COLOR}
        title="By goal — green if on target, red if not"
      />
      {/* "No colour" — draws the metric in neutral grey instead of an accent. */}
      <button
        type="button"
        className={`gmbw-swatch gmbw-swatch-none${value === NO_COLOR ? " on" : ""}`}
        onClick={() => onChange(NO_COLOR)}
        aria-label="No colour"
        aria-pressed={value === NO_COLOR}
        title="No colour"
      />
    </div>
  );
};
ColourPicker.propTypes = {
  value: PropTypes.string,
  fallback: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
ColourPicker.defaultProps = { value: null, fallback: null };

/* What sits under the numbers. One at a time — two blocks push the card past
   whatever shares its row, and then it dictates the height of the whole row.
   `showOn` is not optional dressing: with more than one metric the block has no
   way to know which one it is about, and picking the first silently is a
   decision nobody reading the card could account for. */
const ShowPicker = ({ value, on, rows, metricOf, onChange, onPickMetric }) => (
  <>
    <div className="gmbw-showlist">
      {SHOW_OPTIONS.map((o) => (
        <button
          key={o.key || "none"}
          type="button"
          className={`gmbw-show${(value || "") === o.key ? " on" : ""}`}
          onClick={() => onChange(o.key)}
          aria-pressed={(value || "") === o.key}
        >
          <span className="gmbw-show-rad" />
          <span className="gmbw-show-t">
            <b>{o.title}</b>
            <span>{o.blurb}</span>
          </span>
        </button>
      ))}
    </div>
    {!!value && rows.length > 1 && (
      <div className="gmbw-showon">
        <span>Ranked on</span>
        <LightSelect
          value={on || rows[0]?.metricId}
          /* LightSelect emits the whole option object; the setting wants the id. */
          onChange={(o) => onPickMetric(o?.value ?? null)}
          options={rows
            .filter((r) => r.metricId)
            .map((r) => ({ value: r.metricId, label: metricOf(r.metricId)?.label || metricOf(r.metricId)?.title || "Metric" }))}
        />
      </div>
    )}
  </>
);
ShowPicker.propTypes = {
  value: PropTypes.string,
  on: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  metricOf: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onPickMetric: PropTypes.func.isRequired,
};
ShowPicker.defaultProps = { value: "", on: null };

export const GraphPicker = ({ value, onChange }) => {
  const common = GRAPH_OPTIONS.filter((o) => COMMON_GRAPHS.includes(o.value));
  const more = GRAPH_OPTIONS.filter((o) => !COMMON_GRAPHS.includes(o.value));
  const [open, setOpen] = useState(() => more.some((o) => o.value === value));
  const shown = open ? [...common, ...more] : common;

  return (
    <div className="gmbw-graphs-wrap">
      <div className="gmbw-graphs">
        {shown.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`gmbw-graph-opt${value === o.value ? " on" : ""}`}
            onClick={() => onChange(o.value)}
            title={o.label}
            aria-pressed={value === o.value}
          >
            <span className="gmbw-graph-ic"><MiniGraph kind={o.value} /></span>
            <span className="gmbw-graph-lb">{o.label}</span>
          </button>
        ))}
      </div>
      {more.length > 0 && (
        <button type="button" className="gmbw-graphs-more" onClick={() => setOpen(!open)}>
          {open ? "Fewer graphs" : `${more.length} more graphs`}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      )}
    </div>
  );
};


/* A miniature of what each layout actually produces — you pick a picture, not a
   word. Drawn to the card's real proportions: metric blocks up top, the green
   payoff band underneath when a bonus exists. */
/* The live values a layout preview should show — the widget's own metrics and
   bonus, formatted short so they fit the miniature. */
const shortVal = (m) => {
  if (!m) return "—";
  const v = Number(m.value ?? m.amount ?? 0);
  if (m.kind === "$") return v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 ? 1 : 0)}K` : `$${Math.round(v)}`;
  if (m.kind === "%") return `${Math.round(v)}%`;
  return Math.round(v).toLocaleString();
};
const thumbSample = ({ name, rows, metricOf, bonus }) => {
  const face = (r) => {
    const m = r ? metricOf(r.metricId) : null;
    if (!m) return { k: "Metric", v: "—", s: "" };
    const tgt = Number(m.target) || 0;
    return {
      k: (m.label || "").split(" ").slice(0, 2).join(" "),
      v: shortVal({ ...m, value: m.value ?? m.amount }),
      s: tgt ? `of ${shortVal({ kind: m.kind, value: tgt })}` : "this month",
      kind: m.kind,
    };
  };
  const a = face(rows[0]);
  const b = face(rows[1] || rows[0]);
  let earned = "—";
  let pct = "";
  const rule = sanitizeBonus(bonus);
  if (isStepBonus(rule)) {
    /* A step bonus has no live figure in the catalog to multiply — show what
       the ladder tops out at, the same "Up to" the card's own pill says. */
    earned = `Up to ${moneyLabel(Math.max(...rule.steps.map((st) => st.pay)))}`;
    pct = "Steps";
  } else if (rule) {
    pct = `${rule.pct}%`;
    const src = metricOf(rule.ofMetric);
    if (src) {
      const base = Number(src.value ?? src.amount ?? 0);
      earned = `$${Math.round((base * (Number(rule.pct) || 0)) / 100).toLocaleString()}`;
    }
  }
  return { name: (name || "").trim() || "Your component", a, b, earned, pct };
};

/* A small preview of the card each layout produces — the APPROVED treatment:
   drawn in SVG and typeset AT this size (4–7px), with real labels and numbers.
   Never grey placeholder bars, and never a full-size card scaled down: shrinking
   real 14–24px type makes the text collide into mush. Fixed coordinates mean
   nothing can overlap. `sample` carries the widget's own live values so the
   preview shows what THIS admin is actually building. */
/* One size option, drawn as the thing itself: the full row as a faint track,
   and the card at the width this option gives it — with its real name and its
   real number typeset at this scale. Clicking one is choosing a shape you can
   already see, which replaces dragging a resize handle and judging the gap. */
const SizeThumb = ({ step, sample }) => {
  const W = 112;
  const cardW = Math.round((W - 2) * (step / 24));
  const name = ((sample && sample.name) || "Your component").slice(0, step === 6 ? 9 : 18);
  const val = (sample && sample.a && sample.a.v) || "—";
  return (
    <svg viewBox="0 0 112 40" width="100%" height="40" aria-hidden>
      <rect x="1" y="1" width={W - 2} height="38" rx="5" fill="none" stroke="#E2E8F0" strokeDasharray="3 3" />
      <rect x="1" y="1" width={cardW} height="38" rx="5" fill="#fff" stroke="#B9C6D4" />
      <text x="7" y="13" fontSize="5.5" fontWeight="600" fill="#101828">{name}</text>
      <text x="7" y="28" fontSize="10" fontWeight="600" fill="#101828">{val}</text>
    </svg>
  );
};
SizeThumb.propTypes = {
  step: PropTypes.number.isRequired,
  sample: PropTypes.shape({}),
};
SizeThumb.defaultProps = { sample: null };

/* The three widths a custom card can have, as pictures. `floor` is what the
   content demands — two metrics or a bonus cannot fit a quarter, so that
   option explains itself instead of failing silently. */
const SizePicker = ({ value, floor, sample, onPick }) => (
  <div className="gmbw-sizepics">
    {[[6, "Quarter"], [12, "Half"], [24, "Full width"]].map(([step, label]) => {
      const tooSmall = step < floor;
      return (
        <button
          key={step}
          type="button"
          className={`gmbw-sizepic${value === step ? " on" : ""}`}
          disabled={tooSmall}
          title={tooSmall ? "Too small for what's on this card" : undefined}
          onClick={() => onPick(step)}
          aria-pressed={value === step}
        >
          <SizeThumb step={step} sample={sample} />
          <span>{label}</span>
        </button>
      );
    })}
  </div>
);
SizePicker.propTypes = {
  value: PropTypes.number,
  floor: PropTypes.number,
  sample: PropTypes.shape({}),
  onPick: PropTypes.func.isRequired,
};
SizePicker.defaultProps = { value: null, floor: 6, sample: null };

const LayoutThumb = ({ kind, bonus, sample }) => {
  const BR = "var(--gmbw-primary, #1b598e)";
  const PK = "#EC2786";
  const GN = "#12A150";
  const INK = "#101828";
  const MU = "#98A2B3";
  const S = sample || {};
  const nameTxt = S.name || "Your component";
  const aTxt = S.a || { k: "Metric", v: "—", s: "" };
  const bTxt = S.b || { k: "Metric", v: "—", s: "" };
  const earn = S.earned || "—";

  const head = (withPill) => (
    <>
      <text x="8" y="11.5" fontSize="6.5" fontWeight="600" fill={INK}>{nameTxt}</text>
      {withPill && (
        <>
          <rect x="112" y="5" width="30" height="9.5" rx="4.75" fill="rgba(245,179,1,.20)" />
          <text x="127" y="11.8" fontSize="5" fontWeight="600" fill="#B54708" textAnchor="middle">{S.pct || "%"}</text>
        </>
      )}
    </>
  );
  const col = (x, m, frac, colour) => (
    <>
      <text x={x} y="26" fontSize="4.6" fill={MU}>{m.k}</text>
      <text x={x} y="37" fontSize="10.5" fontWeight="600" fill={INK}>{m.v}</text>
      <text x={x} y="43.5" fontSize="4.2" fill={MU}>{m.s}</text>
      <rect x={x} y="46.5" width="62" height="2.6" rx="1.3" fill="#EDF1F5" />
      <rect x={x} y="46.5" width={62 * frac} height="2.6" rx="1.3" fill={colour} />
    </>
  );
  const payBand = (y) => (
    <>
      <rect x="8" y={y} width="134" height="11" rx="3.5" fill="rgba(18,161,80,.10)" stroke="rgba(18,161,80,.30)" />
      <text x="13" y={y + 7.4} fontSize="4.6" fill="#0E7A4C">Earned</text>
      <text x="137" y={y + 7.8} fontSize="7" fontWeight="700" fill={GN} textAnchor="end">{earn}</text>
    </>
  );

  let art;
  if (kind === "meter") {
    art = (
      <>
        {head(true)}
        <text x="8" y="31" fontSize="15" fontWeight="700" fill={GN}>{earn}</text>
        <text x="8" y="39" fontSize="4.4" fill={MU}>{bTxt.v} · {bTxt.s}</text>
        <rect x="8" y="42.5" width="134" height="4.5" rx="2.25" fill="#EDF1F5" />
        <rect x="8" y="42.5" width="83" height="4.5" rx="2.25" fill={BR} />
        <rect x="8" y="53" width="64" height="12" rx="3.5" fill="#fff" stroke="#EFF2F7" />
        <text x="12" y="58.5" fontSize="4" fill={MU}>{aTxt.k}</text>
        <text x="12" y="63.5" fontSize="6" fontWeight="600" fill={INK}>{aTxt.v}</text>
        <rect x="78" y="53" width="64" height="12" rx="3.5" fill="#fff" stroke="#EFF2F7" />
        <text x="82" y="58.5" fontSize="4" fill={MU}>{bTxt.k}</text>
        <text x="82" y="63.5" fontSize="6" fontWeight="600" fill={INK}>{bTxt.v}</text>
      </>
    );
  } else if (kind === "focus") {
    art = (
      <>
        {head(!!bonus)}
        <text x="8" y="25" fontSize="4.6" fill={MU}>{aTxt.k}</text>
        <text x="8" y="40" fontSize="17" fontWeight="600" fill={INK}>{aTxt.v}</text>
        <text x="8" y="47.5" fontSize="4.2" fill={MU}>{aTxt.s}</text>
        {bonus && payBand(53)}
      </>
    );
  } else if (kind === "duo") {
    art = (
      <>
        {head(false)}
        {col(8, aTxt, 0.4, BR)}
        {col(80, bTxt, 0.67, PK)}
        <text x="8" y="62" fontSize="4.2" fill={MU}>This month</text>
      </>
    );
  } else {
    art = (
      <>
        {head(!!bonus)}
        {col(8, aTxt, 0.4, BR)}
        {col(80, bTxt, 0.67, PK)}
        {bonus && payBand(54)}
      </>
    );
  }

  return (
    <span className="gmbw-look-thumb">
      <svg viewBox="0 0 150 70" xmlns="http://www.w3.org/2000/svg" fontFamily='"DM Sans", sans-serif' aria-hidden="true">
        {art}
      </svg>
    </span>
  );
};

/* The layout choice for a "Build your own" widget. It lives on APPEARANCE, with
   the other look-and-feel controls — Basics is for what the widget tracks. */
/* Appearance for a custom card: how each metric is drawn, then what sits under
   the numbers. Everything on this tab answers "how does it look" — Basics keeps
   the question of what the card is scored on. */
export const CustomLayout = ({ draft, metricCatalog = [], onChange }) => {
  const all = Array.isArray(draft.metrics) ? draft.metrics : [];
  const rows = all.filter((r) => r && r.metricId);
  const bonus = draft.bonus || null;
  const looks = layoutsFor(rows, bonus);
  const metricOf = (id) => metricCatalog.find((m) => m.id === id) || null;
  const setRow = (metricId, patch) =>
    onChange({ ...draft, metrics: all.map((r) => (r && r.metricId === metricId ? { ...r, ...patch } : r)) });

  if (!rows.length) {
    return (
      <div className="gmbw-fld">
        <label>Layout</label>
        <p className="gmbw-hint">Pick a metric first — it’s on the Basics tab.</p>
      </div>
    );
  }
  return (
    <>
      {rows.map((r, i) => {
        const mm = metricOf(r.metricId);
        return (
          <div className="gmbw-fld" key={`look-${r.metricId}`}>
            <label>{mm?.label || mm?.title || "Metric"}</label>
            <p className="gmbw-hint" style={{ margin: "0 0 8px" }}>How this one is drawn.</p>
            <div className="gmbw-mrow">
              <div className="gmbw-mrow-g">
                <span>Graph</span>
                <GraphPicker value={r.graph || "number"} onChange={(g) => setRow(r.metricId, { graph: g })} />
                <span>Colour</span>
                <ColourPicker
                  value={r.color}
                  fallback={METRIC_COLORS[i % METRIC_COLORS.length]}
                  onChange={(c) => setRow(r.metricId, { color: c })}
                />
              </div>
            </div>
          </div>
        );
      })}

      <div className="gmbw-fld gmbw-showbox">
        <label>Show more under the numbers</label>
        <p className="gmbw-hint" style={{ margin: "0 0 9px" }}>
          Pick one, or nothing. Two would make the card taller than the one beside it.
        </p>
        <ShowPicker
          value={draft.show || ""}
          on={draft.showOn}
          rows={rows}
          metricOf={metricOf}
          onChange={(v) => onChange({ ...draft, show: v, showOn: v ? (draft.showOn || rows[0]?.metricId || null) : null })}
          onPickMetric={(v) => onChange({ ...draft, showOn: v })}
        />
      </div>

    <div className="gmbw-fld">
      <label>Layout</label>
      {looks.map((L) => (
        <button
          key={L.key}
          type="button"
          className={`gmbw-look${(draft.look || looks[0].key) === L.key ? " on" : ""}`}
          onClick={() => onChange({ ...draft, look: L.key })}
        >
          <LayoutThumb kind={L.key} bonus={!!bonus} sample={thumbSample({ name: draft.title, rows, metricOf, bonus })} />
          <span className="gmbw-look-t">
            <b>{L.label}</b>
            <span>{L.blurb}</span>
          </span>
        </button>
      ))}
    </div>
    </>
  );
};

/* The card's time frame: follow the dashboard, or pin it to a period. Quarter
   and Custom range were added so a component like a "Q4 revenue bonus" can name
   exactly where it reads from — the same range vocabulary the money cards use.
   Custom reveals two date inputs writing a { from, to }. */
const PeriodField = ({ period, customRange, onChange }) => {
  const cr = customRange || {};
  const set = (patch) => onChange(patch);
  return (
    <>
      <LightSelect
        value={period || ""}
        onChange={(o) => set({ period: (o && o.value) || "", customRange })}
        options={[
          { value: "", label: "Match the dashboard" },
          { value: "today", label: "Today" },
          { value: "yesterday", label: "Yesterday" },
          { value: "week", label: "This week" },
          { value: "lastWeek", label: "Last week" },
          { value: "lastMonth", label: "Last month" },
          { value: "month", label: "This month" },
          { value: "quarter", label: "This quarter" },
          { value: "year", label: "This year" },
          { value: "custom", label: "Custom range" },
        ]}
      />
      {period === "custom" && (
        <div className="gmbw-range" style={{ marginTop: 8 }}>
          <input
            type="date"
            aria-label="From"
            max={new Date().toISOString().slice(0, 10)}
            value={cr.from || ""}
            onClick={(e) => { try { e.currentTarget.showPicker(); } catch (_) { /* older browser */ } }}
            onChange={(e) => set({ period: "custom", customRange: { ...cr, from: e.target.value } })}
          />
          <span className="gmbw-range-sep">→</span>
          <input
            type="date"
            aria-label="To"
            max={new Date().toISOString().slice(0, 10)}
            value={cr.to || ""}
            onClick={(e) => { try { e.currentTarget.showPicker(); } catch (_) { /* older browser */ } }}
            onChange={(e) => set({ period: "custom", customRange: { ...cr, to: e.target.value } })}
          />
        </div>
      )}
    </>
  );
};
PeriodField.propTypes = {
  period: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  customRange: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
PeriodField.defaultProps = { period: "", customRange: null };

/* Advanced → a settable payout curve. Off by default (progressive disclosure,
   Victor's call): the common card stays simple, and a comp-plan power user opens
   Advanced and switches it on. Each tier is a threshold → payout, with a % / $
   unit so the same control does percent-of-target OR hard dollar breakpoints. */
/* Icons the tier list needs. Kept inline: they are two shapes, not a set. */
const PcChevron = ({ open }) => (
  <svg className={`gmbw-pc-chev${open ? " open" : ""}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 5l6 5-6 5" /></svg>
);
PcChevron.propTypes = { open: PropTypes.bool };
PcChevron.defaultProps = { open: false };

const PcLock = () => (
  <svg className="gmbw-pc-lock" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9"><rect x="4.5" y="9" width="11" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 016 0V9" strokeLinecap="round" /></svg>
);

/* The unit half of a fused field. It has to render borderless inside the
   field's own border, which LightSelect cannot do (it styles its trigger
   inline), so this is its own small control with the same menu language. */
const UnitSeg = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <span className={`gmbw-useg${open ? " open" : ""}`} ref={ref}>
      <button type="button" className="gmbw-useg-b" aria-label="Unit" onClick={() => setOpen((v) => !v)}>
        {value || "%"}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <span className="gmbw-useg-m">
          {PAYOUT_UNITS.map((u) => (
            <button key={u} type="button" className={`gmbw-useg-i${u === (value || "%") ? " on" : ""}`} onClick={() => { onChange(u); setOpen(false); }}>{u}</button>
          ))}
        </span>
      )}
    </span>
  );
};
UnitSeg.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired };
UnitSeg.defaultProps = { value: "%" };

const pcMoney = (v) => {
  const n = Number(String(v).replace(/[^0-9.-]/g, "")) || 0;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const PayoutCurveAdvanced = ({ value, onChange, layout, onLayout, metricOptions, multiWs }) => {
  /* Who signed a manual gate off — recorded with the confirmation so the card
     can name them instead of saying an anonymous "confirmed". */
  const me = useSelector((st) => st.authReducer?.userDetails) || null;
  const meId = me?._id ? String(me._id) : "";
  const meName = [me?.firstName, me?.lastName].filter(Boolean).join(" ");
  const [open, setOpen] = useState(!!(value && value.on));
  const [openT, setOpenT] = useState(-1); // one tier open at a time
  const on = !!(value && value.on);
  const tiers = (value && value.tiers) || [];
  const maxTierPay = tiers.reduce((m, t) => Math.max(m, Number(t.pay) || 0), 0) || 1;
  const pl = { ...defaultPieceLayout(), ...(layout || {}) };
  const pct = Math.round(Math.max(0.25, Math.min(0.75, pl.split)) * 100);
  const setLayout = (patch) => onLayout({ ...pl, ...patch });

  const setTiers = (next) => onChange({ on: true, tiers: next });
  const toggle = () => (on ? onChange(null) : onChange(defaultPayoutCurve()));
  const editTier = (i, patch) => setTiers(tiers.map((t, x) => (x === i ? { ...t, ...patch } : t)));
  const addTier = () => {
    const maxAt = tiers.reduce((m, t) => Math.max(m, Number(t.at) || 0), 0);
    const maxPay = tiers.reduce((m, t) => Math.max(m, Number(t.pay) || 0), 0);
    const unit = tiers.length ? tiers[tiers.length - 1].unit : "%";
    setTiers([...tiers, { at: unit === "%" ? maxAt + 5 : maxAt + 50000, unit, pay: maxPay + 2000 }]);
  };
  const removeTier = (i) => setTiers(tiers.filter((_, x) => x !== i));

  /* Gates a tier carries. Any tier can require sales health before it pays, not
     only the ones over 100%. The tier keeps its own gates so 120% can differ
     from 110%. Crediting a gated tier is server work, kept for later. */
  /* Turning gating on starts EMPTY on purpose: the empty state tells you the
     tier pays on revenue alone until you add a check. */
  const toggleTierGate = (i) => {
    const t = tiers[i];
    editTier(i, { gate: !t.gate, gates: t.gates || [] });
  };
  const setGates = (i, next) => editTier(i, { gates: next });
  const addGate = (i) => setGates(i, [...((tiers[i] && tiers[i].gates) || []), defaultGate()]);
  const editGate = (i, gi, patch) => setGates(i, ((tiers[i] && tiers[i].gates) || []).map((g, x) => (x === gi ? { ...g, ...patch } : g)));
  const removeGate = (i, gi) => setGates(i, ((tiers[i] && tiers[i].gates) || []).filter((_, x) => x !== gi));

  return (
    <div className={`gmbw-adv${open ? " open" : ""}`}>
      <button type="button" className="gmbw-adv-h" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Advanced settings
        <svg className="gmbw-adv-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      <div className="gmbw-adv-body">
        <div className="gmbw-bonusbar">
          <span className="gmbw-adv-toglab">Add a payout curve</span>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label="Add a payout curve"
            className={`gmbw-switch${on ? " on" : ""}`}
            onClick={toggle}
          >
            <span className="gmbw-switch-dot" />
          </button>
        </div>
        <p className="gmbw-hint" style={{ margin: "2px 0 0" }}>Set tiered payouts driven by this component’s metric.</p>

        {on && (
          <div className="gmbw-pl">
            <div className="gmbw-pc-h">Layout</div>
            <div className="gmbw-pl-row">
              <div className="gmbw-plseg">
                <button type="button" className={pl.dir === "side" ? "on" : ""} onClick={() => setLayout({ dir: "side" })}>Side by side</button>
                <button type="button" className={pl.dir === "stack" ? "on" : ""} onClick={() => setLayout({ dir: "stack" })}>Stacked</button>
              </div>
              <button type="button" className="gmbw-plswap" onClick={() => setLayout({ swap: !pl.swap })}>
                <Icon d={IcSwap} size={13} /> Swap
              </button>
            </div>
            {pl.dir === "side" && (
              <div className="gmbw-plsplit">
                <span className="gmbw-plsplit-l">Metric {pct}%</span>
                <input
                  type="range"
                  min="25"
                  max="75"
                  step="1"
                  value={pct}
                  onChange={(e) => setLayout({ split: Number(e.target.value) / 100 })}
                />
                <span className="gmbw-plsplit-l">Curve {100 - pct}%</span>
              </div>
            )}
          </div>
        )}

        {on && (
          <div className="gmbw-pc">
            <div className="gmbw-pc-h">Payout tiers</div>

            {/* The shape of the ladder: each step sized by what it pays, a lock
                on the gated ones, the open one lit. The list and the shape
                point at each other, so a wrong number is visible at a glance. */}
            {tiers.length > 0 && (
              <>
                <div className="gmbw-pcl">
                  {tiers.map((t, i) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className={`gmbw-pcl-r${t.gate ? " g" : ""}${openT === i ? " cur" : ""}`}
                      style={{ height: `${Math.max(8, Math.round(((Number(t.pay) || 0) / maxTierPay) * 100))}%` }}
                    >
                      {t.gate && <span className="gmbw-pcl-lk"><PcLock /></span>}
                    </div>
                  ))}
                </div>
                <div className="gmbw-pcl-lb">
                  {tiers.map((t, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={i}>{t.at}{t.unit || "%"}</span>
                  ))}
                </div>
              </>
            )}

            {tiers.length === 0 ? (
              <div className="gmbw-pc-emptylg">
                <b>No tiers yet</b>
                <span>Add the first one to start the ladder.</span>
              </div>
            ) : (
              <div className="gmbw-pc-tiers">
                {tiers.map((t, i) => {
                  const gts = t.gates || [];
                  const isOpen = openT === i;

                  if (!isOpen) {
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div className="gmbw-pc-tier" key={i}>
                        <div
                          className="gmbw-pc-sum"
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpenT(i)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenT(i); } }}
                        >
                          <span className="gmbw-pc-at2">{t.at}{t.unit || "%"}</span>
                          <span className="gmbw-pc-ar">&#8594;</span>
                          <span className="gmbw-pc-pay2">${pcMoney(t.pay)}</span>
                          {t.gate && <span className="gmbw-pc-tag"><PcLock />{gts.length || "no"} gates</span>}
                          <PcChevron />
                        </div>
                      </div>
                    );
                  }

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className="gmbw-pc-tier open" key={i}>
                      {/* Open: the row edits in place, so the same two numbers
                          are never printed twice. */}
                      <div className="gmbw-pc-sum open">
                        <span className="gmbw-pcf gmbw-pcf-at">
                          <input
                            className="gmbw-pcf-num"
                            type="number"
                            min="0"
                            value={t.at ?? ""}
                            onChange={(e) => editTier(i, { at: Number(e.target.value) })}
                          />
                          <UnitSeg value={t.unit || "%"} onChange={(u) => editTier(i, { unit: u })} />
                        </span>
                        <span className="gmbw-pc-ar">&#8594;</span>
                        <span className="gmbw-pcf gmbw-pcf-pay">
                          <span className="gmbw-pcf-pre">$</span>
                          <input
                            className="gmbw-pcf-num right"
                            type="number"
                            min="0"
                            step="25"
                            value={t.pay ?? ""}
                            onChange={(e) => editTier(i, { pay: Number(e.target.value) })}
                          />
                        </span>
                        <span className="gmbw-pc-spacer" />
                        {t.gate && <span className="gmbw-pc-tag"><PcLock />{gts.length || "no"}</span>}
                        <button type="button" className="gmbw-pc-collapse" aria-label="Close tier" onClick={() => setOpenT(-1)}>
                          <PcChevron open />
                        </button>
                      </div>

                      <div className="gmbw-pc-body">
                        <div className="gmbw-pc-gaterow">
                          <span className="gmbw-pc-gatetxt">
                            <b>Gate this tier</b>
                            <i>Only pay it when the checks below pass.</i>
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!t.gate}
                            aria-label="Gate this tier"
                            className={`gmbw-switch${t.gate ? " on" : ""}`}
                            onClick={() => toggleTierGate(i)}
                          >
                            <span className="gmbw-switch-dot" />
                          </button>
                        </div>

                        {t.gate && (gts.length === 0 ? (
                          <div className="gmbw-pc-empty">
                            <p><b>No gates on this tier yet</b> It pays on revenue alone until you add one.</p>
                            <button type="button" className="gmbw-pc-gadd" onClick={() => addGate(i)}>
                              <Icon d={IcPlus} size={12} /> Add a gate
                            </button>
                          </div>
                        ) : (
                          <div className="gmbw-pc-glist">
                            {gts.map((g, gi) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div className={`gmbw-pc-gate${g.on === false ? " off" : ""}`} key={gi}>
                                <div className="gmbw-pc-gtop">
                                  <input
                                    className="gmbw-pc-gname"
                                    value={g.name || ""}
                                    placeholder="Gate name"
                                    onChange={(e) => editGate(i, gi, { name: e.target.value })}
                                  />
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={g.on !== false}
                                    aria-label="Enable gate"
                                    className={`gmbw-switch sm${g.on !== false ? " on" : ""}`}
                                    onClick={() => editGate(i, gi, { on: g.on === false })}
                                  >
                                    <span className="gmbw-switch-dot" />
                                  </button>
                                  <button type="button" className="gmbw-pc-gx" aria-label="Remove gate" onClick={() => removeGate(i, gi)}>&times;</button>
                                </div>
                                <span className="gmbw-pc-gl">Source</span>
                                <LightSelect
                                  options={GATE_SOURCES}
                                  value={g.source || "metric"}
                                  onChange={(o) => editGate(i, gi, { source: o.value })}
                                  width="100%"
                                  height={34}
                                  menuWidth={250}
                                  fontSize={12.5}
                                  radius={8}
                                />

                                {(g.source || "metric") === "manual" ? (
                                  /* No metric exists for this one, so there is no
                                     number to read and nothing to compare. It is
                                     marked met or not met, and it expires with the
                                     period so an old sign-off can't pay a new one. */
                                  <div className="gmbw-pc-gman">
                                    <b>Someone marks this met or not met</b>
                                    <i>No minimum and no target, because there is no number to read.</i>
                                    {/* The sign-off itself. Filed under the gate's
                                        own period key, so next quarter starts
                                        unconfirmed and an old tick can never pay a
                                        new one. */}
                                    {(() => {
                                      const key = gatePeriodKey(g);
                                      const hit = (g.confirmations || {})[key];
                                      return (
                                        <div className={`gmbw-pc-gconf${hit ? " on" : ""}`}>
                                          <span>
                                            {hit
                                              ? `Confirmed ${hit.at ? new Date(hit.at).toLocaleDateString() : ""}${hit.byName ? ` by ${hit.byName}` : ""}`
                                              : `Not confirmed for ${gatePeriodLabel(g.period).toLowerCase()}`}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const next = { ...(g.confirmations || {}) };
                                              if (hit) delete next[key];
                                              else next[key] = { at: new Date().toISOString(), by: meId || "", byName: meName || "" };
                                              editGate(i, gi, { confirmations: next });
                                            }}
                                          >
                                            {hit ? "Undo" : "Mark met"}
                                          </button>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <>
                                    <span className="gmbw-pc-gl">Reads</span>
                                    <LightSelect
                                      options={metricOptions}
                                      value={g.metric || ""}
                                      onChange={(o) => {
                                        const clean = o.metricName || o.label;
                                        editGate(i, gi, { metric: o.value, metricName: clean, unit: unitForMetricLabel(clean) });
                                      }}
                                      placeholder="Pick a metric"
                                      width="100%"
                                      height={34}
                                      menuWidth={250}
                                      fontSize={12.5}
                                      radius={8}
                                    />
                                    {multiWs && (
                                      <>
                                        <span className="gmbw-pc-gl">
                                          When it lives on more than one of these workspaces
                                        </span>
                                        <LightSelect
                                          options={GATE_MODES}
                                          value={g.mode === "sum" ? "sum" : "avg"}
                                          onChange={(o) => editGate(i, gi, { mode: o.value })}
                                          width="100%"
                                          height={34}
                                          menuWidth={250}
                                          fontSize={12.5}
                                          radius={8}
                                        />
                                      </>
                                    )}
                                    <div className="gmbw-pc-grow">
                                      <span className="gmbw-pc-gcol">
                                        <span className="gmbw-pc-gl">Minimum <em>unlocks</em></span>
                                        <span className="gmbw-pcf">
                                          <span className="gmbw-pcf-pre">min</span>
                                          <input
                                            className="gmbw-pcf-num"
                                            type="number"
                                            value={gateMin(g) ?? ""}
                                            onChange={(e) => editGate(i, gi, { min: e.target.value === "" ? null : Number(e.target.value) })}
                                          />
                                          <UnitSeg value={g.unit || "%"} onChange={(u) => editGate(i, gi, { unit: u })} />
                                        </span>
                                      </span>
                                      <span className="gmbw-pc-gcol">
                                        {/* Empty IS "no target" — that is the whole
                                            way to say it, so there is no toggle. */}
                                        <span className="gmbw-pc-gl">Target <em>optional</em></span>
                                        <span className="gmbw-pcf">
                                          <span className="gmbw-pcf-pre">goal</span>
                                          <input
                                            className="gmbw-pcf-num"
                                            type="number"
                                            placeholder="none"
                                            value={g.target ?? ""}
                                            onChange={(e) => editGate(i, gi, { target: e.target.value === "" ? null : Number(e.target.value) })}
                                          />
                                          <UnitSeg value={g.unit || "%"} onChange={(u) => editGate(i, gi, { unit: u })} />
                                        </span>
                                      </span>
                                    </div>
                                  </>
                                )}

                                <span className="gmbw-pc-gl">
                                  {(g.source || "metric") === "manual" ? "Confirmed every" : "Measured over"}
                                </span>
                                <LightSelect
                                  options={GATE_PERIODS}
                                  value={g.period || "quarter"}
                                  onChange={(o) => editGate(i, gi, { period: o.value })}
                                  width="100%"
                                  height={34}
                                  menuWidth={250}
                                  fontSize={12.5}
                                  radius={8}
                                />
                                {g.period === "custom" && (
                                  <div className="gmbw-pc-gdates">
                                    <input type="date" value={g.from || ""} onChange={(e) => editGate(i, gi, { from: e.target.value })} />
                                    <span>to</span>
                                    <input type="date" value={g.to || ""} onChange={(e) => editGate(i, gi, { to: e.target.value })} />
                                  </div>
                                )}
                                <p className="gmbw-pc-ghint">
                                  {(g.source || "metric") === "manual"
                                    ? "Each new period starts unconfirmed and has to be marked again."
                                    : "This window is the gate’s own. It never follows the board filter, or a viewer changing their view would change who gets paid."}
                                </p>
                              </div>
                            ))}
                            <button type="button" className="gmbw-pc-gadd" onClick={() => addGate(i)}>
                              <Icon d={IcPlus} size={12} /> Add a gate
                            </button>
                          </div>
                        ))}

                        <div className="gmbw-pc-tfoot">
                          <button type="button" className="gmbw-pc-rmtier" onClick={() => { removeTier(i); setOpenT(-1); }}>Remove tier</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button type="button" className="gmbw-pc-addtier" onClick={() => { setOpenT(tiers.length); addTier(); }}>
              <Icon d={IcPlus} size={13} /> Add a tier
            </button>
            <p className="gmbw-hint" style={{ margin: "10px 0 0" }}>
              Below the lowest tier pays $0. Open a tier to set what it pays, and what has to pass before it does.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
PayoutCurveAdvanced.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  layout: PropTypes.object,
  onLayout: PropTypes.func.isRequired,
  metricOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    metricName: PropTypes.string,
  })),
  multiWs: PropTypes.bool,
};
PayoutCurveAdvanced.defaultProps = { value: null, layout: null, metricOptions: [], multiWs: false };

/* Editing a "Build your own" widget: the same pieces as the create flow —
   up to MAX_METRICS metric rows (each with its graph), an optional bonus, and
   the layout —
   but inline in the editor's Basics tab, so the board updates as you change it. */
/* The workspaces a card tracks, as [{ id, name }] in the company's own order —
   what the Beat the Yeti goal boxes list. An empty pick falls back to the one
   workspace the editor is on, the same as the card does. */
const trackedWorkspaces = (ids, all) => {
  const want = new Set((ids || []).filter(Boolean).map(String));
  return (all || [])
    .filter((w) => want.has(String(w._id)))
    .map((w) => ({ id: String(w._id), name: w.name || "Workspace" }));
};

export const CustomFields = ({ draft, metricCatalog = [], onChange }) => {
  const selected = UseSelectedWorkspace();
  const workspaces = UseCompanyWorkspaces(true) || [];
  const wsKey = (Array.isArray(draft.workspaceIds) ? draft.workspaceIds : []).map(String).filter(Boolean).sort().join(",");
  const fallbackWs = selected?._id || draft.workspaceId || null;
  const scopedCatalog = useMemo(() => {
    const list = metricsForWorkspaces(metricCatalog, wsKey ? wsKey.split(",") : [], fallbackWs);
    let scopeIds = [];
    if (wsKey) scopeIds = wsKey.split(",").filter(Boolean);
    else if (fallbackWs) scopeIds = [String(fallbackWs)];
    const wsLabel = (id) => {
      const w = workspaces.find((x) => String(x._id) === String(id));
      return (w && w.name) || null;
    };
    /* Single-team card: header is that workspace's name. Multi: keep per-metric
       labels, but never render a raw object as `[object Object]`. */
    if (scopeIds.length === 1) {
      const name = wsLabel(scopeIds[0]) || selected?.name || "Metrics";
      return list.map((m) => ({ ...m, group: name }));
    }
    return list.map((m) => {
      const fromWs = (m.workspaceIds || []).length === 1 ? wsLabel(m.workspaceIds[0]) : null;
      return { ...m, group: metricGroupLabel(fromWs || m.group) };
    });
  }, [metricCatalog, wsKey, fallbackWs, workspaces, selected?.name]);
  const rows = Array.isArray(draft.metrics) && draft.metrics.length
    ? draft.metrics
    : [{ metricId: null, graph: "number" }];
  const chosenRows = rows.filter((r) => r.metricId);
  const bonus = draft.bonus || null;
  const looks = layoutsFor(chosenRows, bonus);
  const metricOf = (id) => scopedCatalog.find((m) => m.id === id) || metricCatalog.find((m) => m.id === id) || null;
  const setRows = (next) => onChange({ ...draft, metrics: next });
  const available = (i) => scopedCatalog.filter((m) => !rows.some((r, x) => x !== i && r.metricId === m.id));
  const wsIds = wsKey ? wsKey.split(",").filter(Boolean) : [];
  /* Pairing builder vs plain metric list — see usesMetricPairing. A card on a
     derived metric (Beat the Yeti rate…) keeps the plain list even over several
     workspaces, so the editor shows the card's real metric. */
  const pairing = usesMetricPairing(wsIds, chosenRows);

  // Drop picks that no longer belong to the scoped workspaces.
  // Drop a cross-workspace metricPlan when this card is back to one team —
  // otherwise the live card still renders the old single paired column.
  const scopedIdsKey = scopedCatalog.map((m) => m.id).join("|");
  useEffect(() => {
    const allowed = new Set(scopedCatalog.map((m) => m.id));
    const next = rows.map((r) => (r.metricId && !allowed.has(r.metricId) ? { ...r, metricId: null, graph: "number" } : r));
    const metricsChanged = next.some((r, i) => r.metricId !== rows[i].metricId);
    if (wsIds.length < 2 && hasMetricPlan(draft.metricPlan)) {
      onChange({ ...draft, metrics: metricsChanged ? next : rows, metricPlan: null });
      return;
    }
    if (metricsChanged) setRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedIdsKey, wsIds.length]);

  /* Do NOT auto-collapse two metric rows into a cross-workspace "paired" plan.
     That path turned Inside Sales (Estimate Won + Closed Rate) into one 0% tile.
     Admins who want pairing use MetricPlanEditor explicitly (2+ workspaces). */

  return (
    <>
      {/* Workspaces first so the metric picker only offers KPIs those
          workspaces track. */}
      <div className="gmbw-fld">
        <ScopeWindow draft={draft} onChange={onChange} fields={["workspaces", "show"]} />
      </div>

      {/* Two+ workspaces: pair one metric PER team into ONE number on the card.
          Adding Sold Revenue + Estimate Won Value as two separate rows never
          combined them — that was the "Not available yet" trap. MetricPlan is
          the real pairing control; we sync `metrics` to the plan column keys. */}
      {pairing ? (
        <MetricPlanEditor
          draft={draft}
          metricCatalog={metricCatalog}
          onChange={(next) => {
            if (hasMetricPlan(next.metricPlan)) {
              const cols = (next.metricPlan.cols || []).filter((c) => c && c.show !== false);
              onChange({
                ...next,
                metrics: cols.length
                  ? cols.map((c) => ({ metricId: c.key, graph: "number" }))
                  : [{ metricId: null, graph: "number" }],
              });
            } else {
              /* Pass metricPlan through as-is (don't null it) — switching to
                 Automatic leaves it remembered-but-off so Custom can restore
                 it later instead of reseeding from scratch. */
              onChange(next);
            }
          }}
        />
      ) : (
      <div className="gmbw-fld">
        <label>Metrics</label>
        {rows.map((r, i) => {
          const m = metricOf(r.metricId);
          return (
            /* eslint-disable-next-line react/no-array-index-key */
            <div className="gmbw-mrow" key={`erow${i}`}>
              <div className="gmbw-mrow-top">
                <MetricPicker
                  metrics={available(i)}
                  value={r.metricId}
                  onPick={(mm) => setRows(rows.map((x, ix) => (ix === i ? { ...x, metricId: mm.id, graph: defaultGraphFor(mm) } : x)))}
                />
                {rows.length > 1 && (
                  <button type="button" className="gmbw-mrow-x" aria-label="Remove this metric" onClick={() => setRows(rows.filter((_, ix) => ix !== i))}>
                    <Icon d={IcX} size={11} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {rows.length < MAX_METRICS && (
          <button type="button" className="gmbw-addrow" onClick={() => setRows([...rows, { metricId: null, graph: "number" }])}>
            <Icon d={IcPlus} size={13} /> Add another metric
          </button>
        )}
        {!scopedCatalog.length && (
          <p className="gmbw-hint">No metrics on the selected workspace(s) yet.</p>
        )}
      </div>
      )}

      {pairing && (
        <p className="gmbw-hint" style={{ marginTop: -4, marginBottom: 12 }}>
          Switch to <b>Custom</b> and pick Sold Revenue on Sales + Estimate Won Value on In-House (or whatever each team uses) — that becomes <b>one</b> number on the card and drives the payout curve.
        </p>
      )}

      {/* Only for the Beat the Yeti rate metric — nothing else has a team count. */}
      {chosenRows.some((r) => r.metricId === BEAT_YETI_RATE_ID) && (
        <YetiOptionsField
          value={draft}
          workspaces={trackedWorkspaces(wsIds.length ? wsIds : [fallbackWs], workspaces)}
          onChange={(patch) => onChange({ ...draft, ...patch })}
        />
      )}

      {/* Only for Technicians' Revenue per Hour — a computed metric with no goal
          of its own to edit. */}
      {hasRphMetric(chosenRows) && (
        <RphGoalField
          value={Number(draft.rphGoal) > 0 ? Number(draft.rphGoal) : null}
          onChange={(g) => onChange({ ...draft, rphGoal: g })}
        />
      )}

      <div className="gmbw-fld">
        <BonusField
          value={bonus}
          onChange={(b) => onChange({ ...draft, bonus: b })}
          chosen={chosenRows.map((r) => metricOf(r.metricId))}
          catalog={scopedCatalog}
          metricOf={metricOf}
        />
      </div>

      {/* The same Filters group the create flow has. It was only ever built
          there, so a card you edited afterwards had no way to change its period
          or turn the player picker off — the setting existed but was
          unreachable once the card was on the board. */}
      <div className="gmbw-fld">
        <label>Filters</label>
        <p className="gmbw-hint" style={{ margin: "0 0 9px" }}>Which numbers this card is about.</p>
        <PeriodField
          period={draft.period || ""}
          customRange={draft.customRange || null}
          onChange={({ period, customRange }) => onChange({ ...draft, period, customRange })}
        />
        <p className="gmbw-hint" style={{ margin: "7px 0 10px" }}>
          Match the dashboard and the filter at the top of the board changes this card too.
        </p>
        <button
          type="button"
          className={`gmbw-whotog${draft.who !== false ? " on" : ""}`}
          onClick={() => onChange({ ...draft, who: draft.who === false })}
          aria-pressed={draft.who !== false}
        >
          <span className="gmbw-whotog-t">
            <b>Let admins switch player</b>
            <span>Admins get a name dropdown on the card. Players always see their own numbers.</span>
          </span>
          <span className="gmbw-whotog-sw"><i /></span>
        </button>
      </div>

      <div className="gmbw-fld">
        <PayoutCurveAdvanced
          value={draft.payoutCurve || null}
          onChange={(pc) => onChange({ ...draft, payoutCurve: pc })}
          layout={draft.pieceLayout || null}
          onLayout={(pl) => onChange({ ...draft, pieceLayout: pl })}
          metricOptions={scopedCatalog.map((m) => {
            const base = m.label || m.name || String(m.id);
            const tag = metricWorkspaceTag(m, workspaces);
            return { value: m.id, label: tag ? `${base} — ${tag}` : base, metricName: base };
          })}
          multiWs={wsIds.length >= 2}
        />
        {draft.payoutCurve && draft.payoutCurve.on && (
          <div style={{ marginTop: 12 }}>
            <label className="gmbw-blabel" htmlFor="gmbw-goal-target">Bonus target for this card</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="gmbw-pc-dollar">$</span>
              <input
                id="gmbw-goal-target"
                className="gmbw-input"
                type="number"
                min="1"
                step="1000"
                required
                placeholder="e.g. 1000000"
                value={draft.goalTarget != null && draft.goalTarget !== "" ? draft.goalTarget : ""}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  onChange({
                    ...draft,
                    goalTarget: Number.isFinite(n) && n > 0 ? n : null,
                  });
                }}
              />
            </div>
            <p className="gmbw-hint" style={{ marginTop: 6 }}>
              Required when a payout curve is on. Sets the “of $X” figure and the dollar amounts under each % tier (target × %).
            </p>
            {!(Number(draft.goalTarget) > 0) && (
              <p className="gmbw-hint" style={{ marginTop: 4, color: "#F04438" }}>
                Enter a bonus target so % tiers and the curve can place correctly.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

/* One row for a component — state-aware. Added → a green tick and Edit/Remove;
   not added → drag or Add, and Set up first for the editable ones. */
const LibItem = ({ w, added, copies = 1, onAdd, onConfigure, onEdit, onRemove, onDragWidget, onCreate }) => {
  const Thumb = w.thumb;
  const editable = isEditable(w.id);
  // The blank "Build your own" card never lands as itself — it opens the create
  // flow, which mints a real instance. So it is always addable and not draggable.
  let primary;
  if (w.blank) primary = () => onCreate();
  else if (added) primary = () => (editable ? onEdit(w.id) : null);
  else primary = (e) => onAdd(w.id, e);

  let acts;
  if (w.blank) {
    acts = (
      <button type="button" className="primary" title="Create a component" aria-label="Create a component"
        onClick={(e) => { e.stopPropagation(); onCreate(); }}>
        <Icon d={IcPlus} />
      </button>
    );
  } else if (added) {
    acts = (
      <>
        {editable && (
          <button type="button" title="Edit this component" aria-label={`Edit ${w.title}`}
            onClick={(e) => { e.stopPropagation(); onEdit(w.id); }}>
            <Icon d={IcGear} />
          </button>
        )}
        {/* Already on the board is no longer a dead end: a component can be
            added again, and each copy keeps its own settings. */}
        <button type="button" className="primary" title="Add another copy" aria-label={`Add another ${w.title}`}
          onClick={(e) => { e.stopPropagation(); onAdd(w.id, e); }}>
          <Icon d={IcPlus} />
        </button>
        <button type="button" className="danger" title="Remove from board" aria-label={`Remove ${w.title}`}
          onClick={(e) => { e.stopPropagation(); onRemove(w.id); }}>
          <Icon d={IcX} />
        </button>
      </>
    );
  } else {
    acts = (
      <>
        {editable && (
          <button type="button" title="Set it up before it lands" aria-label={`Set up ${w.title}`}
            onClick={(e) => { e.stopPropagation(); onConfigure(w.id); }}>
            <Icon d={IcGear} />
          </button>
        )}
        <button type="button" className="primary" title="Add to the board now" aria-label={`Add ${w.title}`}
          onClick={(e) => { e.stopPropagation(); onAdd(w.id, e); }}>
          <Icon d={IcPlus} />
        </button>
      </>
    );
  }
  return (
    <div
      className={`gmbw-lib-item${added ? " added" : ""}`}
      role="button"
      tabIndex={0}
      draggable={!added && !w.blank}
      onClick={primary}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          primary();
        }
      }}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", w.id);
        e.dataTransfer.effectAllowed = "copy";
        onDragWidget?.(w.id);
      }}
      onDragEnd={() => onDragWidget?.(null)}
    >
      <span className="gmbw-lib-pv" aria-hidden="true">
        <span className="gmbw-lib-frame">{Thumb ? <Thumb /> : null}</span>
      </span>
      <span className="gmbw-lib-row">
        <span className="gmbw-lib-txt">
          <span className="gmbw-lib-name">
            {w.title}
            {added && (
              <span className="gmbw-added-badge">
                <Icon d={IcCheck} size={11} /> {copies > 1 ? `Added ×${copies}` : "Added"}
              </span>
            )}
          </span>
          <span className="gmbw-lib-desc">{w.blurb}</span>
        </span>
        <span className="gmbw-lib-acts">{acts}</span>
      </span>
    </div>
  );
};

/* ── the editor ───────────────────────────────────────────────────────────
   No preview pane on purpose: the card is on the board a few inches away and
   repaints as you type. A mock preview in here would be a worse copy of
   something already on screen — except when the component is not placed yet,
   which is what the dashed ghost card on the board is for. */
const SIZE_LABELS = [["compact", "Compact"], ["standard", "Standard"], ["feature", "Feature"]];

/* Two tabs — "What it shows" (the data) and "How it looks" (the form) — the
   structure Victor approved. No preview pane on purpose: the card is on the
   board a few inches away and repaints as you type; the dashed ghost card
   covers the not-yet-placed case. */
const Editor = ({ def, draft, pending, size, blockW, money, metricCatalog = [], boardWidgets = [], savedAt, onChange, onSize, onWidth, onMoney, onCancel, onSave, onClose }) => {
  /* Saving keeps you on the component now, so the click needs its own
     acknowledgement — the Save button flips to "Saved ✓" for a beat, then
     settles back. It is a receipt, not state, so it clears itself. */
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!savedAt) return undefined;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1500);
    return () => clearTimeout(t);
  }, [savedAt]);

  // Which renamable chips have their title field dropped open. The pencil next
  // to a toggle flips it; a chip that already carries a custom title opens
  // showing that title so it reads as "edited".
  const [openLabels, setOpenLabels] = useState({});
  const toggleLabel = (key) => setOpenLabels((o) => ({ ...o, [key]: !o[key] }));

  const scenes = def.scenes || [];
  // Money cards get a third tab. It opens on Setup when the program is not
  // configured yet — that is the reason the admin came here.
  const isMoney = !!(def.money && money);
  const isAnnual = !!def.annualSetup;
  // The leaderboard has rules of its own — same third tab, different contents.
  /* The leaderboard's cross-workspace half. The mode lives in the EDITOR, not
     in which dashboard the card happens to sit on — pick "Across workspaces"
     here and this card becomes the metric table, exactly as it does on an
     Analytics board. */
  const boardCfg = def.board || null;
  const isLeaderboard = !!boardCfg;
  const across = isLeaderboard && !!draft.across;
  const isBoard = across;
  /* Labor keeps its departments — and the workspace each one reads — in Setup. */
  const isLabor = !!def.laborSetup;
  /* Revenue, Expenses & Labor keeps its sources, expenses and goals there too. */
  const isProfit = !!def.profitSetup;
  /* All Department Metrics keeps its rows and their metrics there too. */
  const isDept = !!def.deptSetup;
  const unset =
    isMoney &&
    (def.money === "earnings" ? !hasEarningsRules(money.program) : !hasLadder(money.program));
  const [tab, setTab] = useState(unset ? "setup" : "basics");

  /* The metrics the chosen workspaces actually track, read live so the Metrics
     switch describes what it will really put on the card. */
  const live = useLiveColumns(draft, isBoard);

  /* Flipping back to "One workspace" removes the Setup tab — don't strand the
     editor on a tab that no longer exists.

     It must match the condition that RENDERS the tab (isMoney || isBoard || isAnnual).
     Testing only isBoard meant the money cards — Anticipated earnings and
     Milestone bonuses — showed a Setup tab that threw you straight back to
     Basics the moment you pressed it, so their pay rules were unreachable. */
  useEffect(() => {
    if (!isMoney && !isBoard && !isAnnual && !isLabor && !isProfit && !isDept && tab === "setup") setTab("basics");
  }, [isMoney, isBoard, isAnnual, isLabor, isProfit, isDept, tab]);

  const showsFields = def.renameable || def.metricLabel || def.periods;
  let saveLabel = pending ? "Add to board" : "Save changes";
  if (flash) saveLabel = "Saved ✓";

  return (
    <>
      <div className="gmbw-panel-h">
        <div className="gmbw-panel-htxt">
          <button type="button" className="gmbw-backlink" onClick={onCancel}>
            <Icon d={IcBack} size={13} /> All components
          </button>
          <span className="gmbw-panel-title">{pending ? "Set up widget" : "Edit component"}</span>
          <span className="gmbw-panel-sub">{def.title}</span>
          {pending && <span className="gmbw-pendtag">Not on the board yet</span>}
        </div>
        <button type="button" className="gmbw-panel-x" onClick={onClose} aria-label="Close">
          <Icon d={IcX} size={15} />
        </button>
      </div>

      <div className="gmbw-live">
        <i />
        {pending
          ? "The dashed card on the board is yours, taking shape where it will land"
          : "Watch the card on the board — it changes as you edit"}
      </div>

      <div className="gmbw-etabs">
        <button type="button" className={tab === "basics" ? "on" : ""} onClick={() => setTab("basics")}>
          Basics
        </button>
        {(isMoney || isBoard || isAnnual || isLabor || isProfit || isDept) && (
          <button type="button" className={tab === "setup" ? "on" : ""} onClick={() => setTab("setup")}>
            Setup
          </button>
        )}
        <button type="button" className={tab === "appearance" ? "on" : ""} onClick={() => setTab("appearance")}>
          Appearance
        </button>
      </div>

      <div className="gmbw-edit-body">
        {tab === "basics" && (
          <>
            {def.renameable && (
              <div className="gmbw-fld">
                <label htmlFor="gmbw-title">Title on the card</label>
                <input
                  id="gmbw-title"
                  type="text"
                  value={draft.title}
                  placeholder={def.title}
                  maxLength={60}
                  onChange={(e) => onChange({ ...draft, title: e.target.value })}
                />
                <p className="gmbw-hint">Leave empty to keep “{def.title}”.</p>
              </div>
            )}

            {def.custom && (
              <CustomFields draft={draft} metricCatalog={metricCatalog} onChange={onChange} />
            )}

            {def.scope && !isAnnual && <ScopeWindow draft={draft} onChange={onChange} />}

            {/* Which metrics combine across the chosen workspaces (auto by name,
                or paired by hand with a sum/avg per column). Offered on any
                scoped card that reads a shared metric list — not the Mountain,
                which climbs points, nor money / annual-comp cards. */}
            {def.scope && !isAnnual && def.id !== "kotm" && (draft.workspaceIds || []).filter(Boolean).length >= 2 && (
              <MetricPlanEditor draft={draft} onChange={onChange} metricCatalog={metricCatalog} />
            )}

            {isLeaderboard && (
              <div className="gmbw-fld">
                <label>Who it ranks</label>
                <div className="gmbw-showlist">
                  <button
                    type="button"
                    className={`gmbw-show${!across ? " on" : ""}`}
                    aria-pressed={!across}
                    onClick={() => onChange({ ...draft, across: false })}
                  >
                    <span className="gmbw-show-rad" />
                    <span className="gmbw-show-t">
                      <b>One workspace</b>
                      <span>Everyone in a single workspace, ranked by points.</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`gmbw-show${across ? " on" : ""}`}
                    aria-pressed={across}
                    onClick={() => onChange({ ...draft, across: true })}
                  >
                    <span className="gmbw-show-rad" />
                    <span className="gmbw-show-t">
                      <b>Compare across workspaces</b>
                      <span>Pool several teams into one ranking — each row tagged with their workspace. Setup: Yeti qualify gate + Closed Rate tie-break.</span>
                    </span>
                  </button>
                </div>
                {!across && (
                  <p className="gmbw-hint">
                    A one-workspace board has nothing to set up — it is the board you already know.
                  </p>
                )}
              </div>
            )}

            {isBoard && boardCfg.workspaces && <WorkspacePicker draft={draft} onChange={onChange} />}

            {/* The cross-workspace leaderboard's columns: auto-matched by name,
                or paired by hand with a per-column sum/avg. */}
            {isBoard && boardCfg.workspaces && across && (draft.workspaceIds || []).filter(Boolean).length >= 2 && (
              <MetricPlanEditor draft={draft} onChange={onChange} metricCatalog={metricCatalog} />
            )}

            {def.metricLabel && !isMoney && !isLeaderboard && !def.scope && (
              <div className="gmbw-fld">
                <label>Scored on</label>
                <div className="gmbw-lockedfield">
                  <span>{def.metricLabel}</span>
                  <span className="gmbw-soon">Coming soon</span>
                </div>
                <p className="gmbw-hint">This card picks its own metric — you can’t change it yet.</p>
              </div>
            )}
            {isMoney && (
              <div className="gmbw-fld">
                <label>Scored on</label>
                <div className="gmbw-lockedfield">
                  {/* Each money card names ITS OWN metric: the earnings card is
                      paid on the first commission cut in Setup, while the
                      milestone ladder climbs its scored-on metric. One shared
                      label made the earnings card claim the ladder's metric
                      (e.g. "Estimate Won Value" while the cut was Commission). */}
                  <span>{(def.money === "earnings" ? money.earningsScoredOnLabel : money.scoredOnLabel) || "Not set"}</span>
                  <button type="button" className="gmbw-rule-change" onClick={() => setTab("setup")}>Setup</button>
                </div>
                <p className="gmbw-hint">Change this in Setup, with the pay rules.</p>
              </div>
            )}

            {def.periods && !isMoney && (
              <div className="gmbw-fld">
                <label htmlFor="gmbw-period">Period</label>
                <select
                  id="gmbw-period"
                  value={draft.period || ""}
                  onChange={(e) => onChange({ ...draft, period: e.target.value })}
                >
                  <option value="">Match the dashboard filter</option>
                  {def.periods.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <p className="gmbw-hint">Or let it follow the dashboard’s date filter.</p>
              </div>
            )}

            {isMoney && (
              /* The SAME scope panel the Yeti and Mountain editors use, so the
                 settings read identically across every scoped card. These two
                 cards read one workspace's bonus data, so only the controls that
                 apply are shown — Show (average per player vs team total) and the
                 date range. The date range lives here, in the panel, never as a
                 second dropdown stacked on the card header. */
              <ScopeWindow draft={draft} onChange={onChange} fields={["workspaces", "show", "range"]} />
            )}
            {isAnnual && (
              <>
                <p className="gmbw-hint" style={{ marginTop: 0 }}>
                  Year-1 rollup. Pick <b>at least two workspaces</b> (e.g. Sales + In-House), then map which
                  Scorekeeper metrics feed live quarter bonuses — same Metrics builder as Team Performance vs. The Yeti.
                </p>
                <ScopeWindow draft={draft} onChange={onChange} fields={["workspaces", "show", "range"]} />
                {(draft.workspaceIds || []).filter(Boolean).length >= 2 && (
                  <MetricPlanEditor draft={draft} onChange={onChange} minSeedCols={2} metricCatalog={metricCatalog} />
                )}
              </>
            )}
            {!showsFields && !isMoney && !isAnnual && (
              <div className="gmbw-fld">
                <p className="gmbw-hint" style={{ margin: 0 }}>
                  Nothing to set up — this one shows each player their own standing.
                </p>
              </div>
            )}
          </>
        )}

        {tab === "setup" && isBoard && (
          <LeaderboardSetup draft={draft} onChange={onChange} />
        )}

        {tab === "setup" && isMoney && (
          <>
            {def.money === "earnings" ? (
              <EarningsSetup program={money.program} metrics={money.metrics} onChange={onMoney} />
            ) : (
              <LadderSetup program={money.program} metrics={money.metrics} ytd={money.ytd} onChange={onMoney} />
            )}
            <p className="gmbw-hint">
              These rules apply to everyone in <b>{money.workspaceName}</b>.
            </p>
          </>
        )}

        {tab === "setup" && isAnnual && (
          <AnnualCompSetup draft={draft} onChange={onChange} metricCatalog={metricCatalog} />
        )}

        {tab === "setup" && isLabor && (
          <LaborSetup draft={draft} onChange={onChange} />
        )}

        {tab === "setup" && isProfit && (
          <ProfitTrendSetup draft={draft} onChange={onChange} boardWidgets={boardWidgets} />
        )}

        {tab === "setup" && isDept && (
          <DeptMetricsSetup draft={draft} onChange={onChange} />
        )}

        {tab === "appearance" && (
          <>
            {def.custom && (
              <CustomLayout draft={draft} metricCatalog={metricCatalog} onChange={onChange} />
            )}
            {!def.custom && scenes.length > 1 ? (
              <div className="gmbw-fld">
                <label>Scene</label>
                <div className="gmbw-scenes">
                  {scenes.map((sc) => {
                    const Th = sc.thumb;
                    const on = draft.scene === sc.key;
                    return (
                      <button
                        key={sc.key}
                        type="button"
                        className={`gmbw-scene${on ? " on" : ""}`}
                        aria-pressed={on}
                        onClick={() => onChange({ ...draft, scene: sc.key })}
                      >
                        <span className="gmbw-scene-th">{Th ? <Th tiny /> : null}</span>
                        <span className="gmbw-scene-lb">{sc.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="gmbw-hint">The same numbers, drawn a different way.</p>
              </div>
            ) : (!def.custom && (
              <div className="gmbw-fld">
                <label>Scene</label>
                <p className="gmbw-hint">
                  This component has one look.
                </p>
              </div>
            ))}

            {isBoard && Array.isArray(boardCfg?.parts) && boardCfg.parts.length > 0 && (
              <div className="gmbw-fld">
                <label>What to show</label>
                <div className="gmbw-toggles">
                  {boardCfg.parts.map((part, i) => {
                    const shown = !(draft.hide && draft.hide[part.key]);
                    const labelVal = (draft.labels && draft.labels[part.key]) || "";
                    // Renamable + shown + (opened via pencil OR already renamed).
                    const canEdit = !!part.editable && shown;
                    const editing = canEdit && (openLabels[part.key] || !!labelVal);
                    // A switch can need something the board doesn't have yet —
                    // Team and the filter mean nothing on a single workspace.
                    const off = part.needs === "multi" && live.count < 2;
                    // "metrics" is the one description written at render time:
                    // the workspaces own the metric list, so it has to be read,
                    // never spelled out here.
                    let { note } = part;
                    if (part.note === "metrics") {
                      note = live.columns.length
                        ? `A column for each metric these workspaces track: ${live.columns.map((c) => c.label).join(", ")}.`
                        : "These workspaces track no metric in common — nothing to put in columns.";
                    }
                    const newGroup = part.group && (i === 0 || boardCfg.parts[i - 1].group !== part.group);
                    return (
                      <div key={part.key} className="gmbw-toggle-block">
                        {newGroup && <div className="gmbw-toggle-grp">{part.group}</div>}
                        <div className={`gmbw-toggle${note ? " noted" : ""}`}>
                          <span>
                            {part.label}
                            {note && <i className="gmbw-toggle-note">{note}</i>}
                          </span>
                          <div className="gmbw-toggle-ctl">
                            {canEdit && (
                              <button
                                type="button"
                                className={`gmbw-editbtn${editing ? " on" : ""}`}
                                aria-label={`Edit title for ${part.label}`}
                                aria-pressed={editing}
                                title="Edit title"
                                onClick={() => toggleLabel(part.key)}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={shown && !off}
                              aria-label={part.label}
                              disabled={off}
                              className={`gmbw-switch${shown && !off ? " on" : ""}${off ? " off" : ""}`}
                              onClick={() => onChange({ ...draft, hide: { ...(draft.hide || {}), [part.key]: shown } })}
                            >
                              <span className="gmbw-switch-dot" />
                            </button>
                          </div>
                        </div>
                        {/* Title field drops open under the toggle when the pencil
                            is clicked. Empty falls back to the built-in label. */}
                        {editing && (
                          <input
                            type="text"
                            className="gmbw-title-input"
                            value={labelVal}
                            placeholder={part.editable}
                            maxLength={40}
                            /* eslint-disable-next-line jsx-a11y/no-autofocus */
                            autoFocus={!!openLabels[part.key]}
                            aria-label={`Title for ${part.label}`}
                            onChange={(e) =>
                              onChange({ ...draft, labels: { ...(draft.labels || {}), [part.key]: e.target.value } })
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="gmbw-hint">Hiding something changes the card, never the numbers.</p>
              </div>
            )}

            {isDept && <DeptTileStyle draft={draft} onChange={onChange} />}

            {Array.isArray(def?.parts) && def.parts.length > 0 && (
              <div className="gmbw-fld">
                <label>What to show</label>
                <div className="gmbw-toggles">
                  {def.parts.map((part) => {
                    const shown = !(draft.hide && draft.hide[part.key]);
                    const labelVal = (draft.labels && draft.labels[part.key]) || "";
                    const canEdit = !!part.editable && shown;
                    const editing = canEdit && (openLabels[part.key] || !!labelVal);
                    return (
                      <div key={part.key} className="gmbw-toggle-block">
                        <div className="gmbw-toggle">
                          <span>{part.label}</span>
                          <div className="gmbw-toggle-ctl">
                            {canEdit && (
                              <button
                                type="button"
                                className={`gmbw-editbtn${editing ? " on" : ""}`}
                                aria-label={`Edit title for ${part.label}`}
                                aria-pressed={editing}
                                title="Edit title"
                                onClick={() => toggleLabel(part.key)}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={shown}
                              aria-label={part.label}
                              className={`gmbw-switch${shown ? " on" : ""}`}
                              onClick={() => onChange({ ...draft, hide: { ...(draft.hide || {}), [part.key]: shown } })}
                            >
                              <span className="gmbw-switch-dot" />
                            </button>
                          </div>
                        </div>
                        {editing && (
                          <input
                            type="text"
                            className="gmbw-title-input"
                            value={labelVal}
                            placeholder={part.editable}
                            maxLength={40}
                            /* eslint-disable-next-line jsx-a11y/no-autofocus */
                            autoFocus={!!openLabels[part.key]}
                            aria-label={`Title for ${part.label}`}
                            onChange={(e) =>
                              onChange({ ...draft, labels: { ...(draft.labels || {}), [part.key]: e.target.value } })
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="gmbw-hint">Hiding something changes the card, never the numbers. Pencil renames a chip.</p>
              </div>
            )}

            {isMoney && def?.money === "earnings" && (draft.workspaceIds || []).filter(Boolean).length >= 2 && (
              <div className="gmbw-fld">
                <label>Combine on the card</label>
                <div className="gmbw-toggles">
                  <div className="gmbw-toggle-block">
                    <div className="gmbw-toggle noted">
                      <span>
                        Merge rules into one line
                        <i className="gmbw-toggle-note">
                          On: one “How it adds up” row with the money summed. Off: each rule stays separate.
                        </i>
                      </span>
                      <div className="gmbw-toggle-ctl">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!draft.breakdownSplit}
                          aria-label="Merge rules into one line"
                          className={`gmbw-switch${!draft.breakdownSplit ? " on" : ""}`}
                          onClick={() => onChange({ ...draft, breakdownSplit: !draft.breakdownSplit })}
                        >
                          <span className="gmbw-switch-dot" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {!draft.breakdownSplit && (
                  <>
                    <input
                      type="text"
                      className="gmbw-title-input"
                      style={{ marginTop: 8 }}
                      value={draft.breakdownTitle || ""}
                      placeholder="Optional title for the combined line"
                      maxLength={80}
                      onChange={(e) => onChange({ ...draft, breakdownTitle: e.target.value })}
                    />
                    <p className="gmbw-hint">Blank uses the rule names joined with “+”.</p>
                  </>
                )}
              </div>
            )}

            <div className="gmbw-fld">
              <label>Widget size</label>
              {def.custom ? (
                <>
                  <p className="gmbw-hint" style={{ margin: "0 0 8px" }}>
                    How much of the row it takes. You can still drag its corner later.
                  </p>
                  <SizePicker
                    value={blockW || customWidthFor(draft)}
                    floor={customWidthFor(draft)}
                    sample={thumbSample({
                      name: draft.title,
                      rows: (draft.metrics || []).filter((r) => r && r.metricId),
                      metricOf: (mid) => metricCatalog.find((m) => m.id === mid) || null,
                      bonus: draft.bonus,
                    })}
                    onPick={(step) => onWidth?.(def.id, step)}
                  />
                </>
              ) : (
                <>
                  <div className="gmbw-sizes">
                    {SIZE_LABELS.map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        className={size === key ? "on" : ""}
                        aria-pressed={size === key}
                        onClick={() => onSize(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="gmbw-hint">Or drag the card’s corner on the board to size it by hand.</p>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className="gmbw-edit-ft">
        <button type="button" className="gmbw-btn" onClick={onCancel}>Cancel</button>
        {/* The save confirmation IS the button — it flips to "Saved ✓" with a
            soft brand ring, then settles back. No floating toast (Victor's ask):
            the acknowledgement lives exactly where the click happened. */}
        <button
          type="button"
          className={`gmbw-btn primary${flash ? " saved" : ""}`}
          onClick={onSave}
          aria-live="polite"
        >
          {saveLabel}
        </button>
      </div>
    </>
  );
};


/* ── Templates ─────────────────────────────────────────────────────────────
   A whole board, offered the same way a single component is: a preview, a
   name, a line, an action. It used to be a modal that covered the board you
   were about to replace — which is the one thing you want to see.

   The board you are already on is marked and has no button: you cannot apply
   what you already have. The DEFAULT board is simply the last row, which is
   why there is no longer a "Reset to default" lever sitting beside Publish —
   starting over is a choice among boards. */

const sameBoard = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const key = (l) => [...l].map((x) => `${x.i}:${x.x},${x.y},${x.w},${x.h}`).sort().join("|");
  return key(a) === key(b);
};

/* The board, drawn small: every block is the component's own preview at its
   real grid position and width. Owned here rather than imported from the
   template modal — one less thing that can resolve to undefined at runtime,
   and this panel is where it now lives. */
const MiniBoard = ({ layout = [] }) => {
  const blocks = (layout || []).filter((b) => b && widgetById(b.i));
  if (!blocks.length) return null;
  const rows = Math.max(1, ...blocks.map((b) => b.y + b.h));
  return (
    <div className="gmbw-mini" aria-hidden="true">
      {blocks.map((b) => {
        const def = widgetById(b.i);
        const Thumb = def.thumb;
        return (
          <span
            key={`${b.i}-${b.x}-${b.y}`}
            className="gmbw-mini-b"
            style={{
              left: `${(b.x / 24) * 100}%`,
              width: `${(b.w / 24) * 100}%`,
              top: `${(b.y / rows) * 100}%`,
              height: `${(b.h / rows) * 100}%`,
            }}
          >
            <i>{Thumb ? <Thumb /> : <em>{def.title}</em>}</i>
          </span>
        );
      })}
    </div>
  );
};

const TemplateList = ({ current = [], onApply }) => {
  const rows = [
    ...TEMPLATES.filter((t) => Array.isArray(t.layout) && t.layout.length),
    { id: "__default", name: "The default board", blurb: "What every new workspace starts with", layout: DEFAULT_LAYOUT, isDefault: true },
  ];
  return (
    <div className="gmbw-lib-list">
      {rows.map((t) => {
        const layout = conformLayout(t.layout.map((b) => ({ ...b })));
        const inUse = sameBoard(current, layout);
        return (
          <div key={t.id} className={`gmbw-tplrow${inUse ? " on" : ""}`}>
            <span className="gmbw-tplrow-pv">
              <MiniBoard layout={t.layout} />
            </span>
            <div className="gmbw-tplrow-b">
              <span className="gmbw-tplrow-txt">
                <b>
                  {t.name}
                  {inUse && <span className="gmbw-tplchip now">Currently using</span>}
                  {!inUse && t.isDefault && <span className="gmbw-tplchip def">Default</span>}
                  {!inUse && t.recommended && <span className="gmbw-tplchip rec">Recommended</span>}
                </b>
                <span>{t.blurb}</span>
              </span>
              {!inUse && (
                <button type="button" className="gmbw-tpluse" onClick={() => onApply(t.name, t.layout.map((b) => ({ ...b })))}>
                  Use
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const CreatePanel = ({ metrics = [], onCancel, onConfirm, onClose, onDraft, onEnd }) => {
  const selected = UseSelectedWorkspace();
  const workspaces = UseCompanyWorkspaces(true) || [];
  const [tab, setTab] = useState("setup");
  const [name, setName] = useState("");
  const [rows, setRows] = useState([{ metricId: null, graph: "number" }]);
  /* The bonus draft as typed (see BonusField) — null while it is switched off.
     `bonusRule` below is the sanitized, payable version the card is built on. */
  const [bonus, setBonus] = useState(null);
  const [look, setLook] = useState(null);
  /* What sits under the numbers, and which metric it reads. Empty by default —
     a card that shows nothing extra is a real choice, not an unfinished one. */
  const [show, setShow] = useState("");
  const [showOn, setShowOn] = useState(null);
  /* When the card is scored on. Empty means "follow the dashboard filter",
     which is what the control at the top of the board is for; a value here
     pins the card to one period regardless. */
  const [period, setPeriod] = useState("");
  /* A typed range when period === "custom". */
  const [customRange, setCustomRange] = useState(null);
  /* Advanced: a settable payout curve (null until switched on) + how the metric
     and curve sit together (direction · swap · split). */
  const [payoutCurve, setPayoutCurve] = useState(null);
  const [pieceLayout, setPieceLayout] = useState(null);
  const [goalTarget, setGoalTarget] = useState(null);
  /* Which workspaces this component tracks, and whether it averages or totals —
     a head of sales points it at two teams and sees the average across them. */
  const [workspaceIds, setWorkspaceIds] = useState([]);
  const [avgMode, setAvgMode] = useState("avg");
  const [metricPlan, setMetricPlan] = useState(null);
  /* Beat the Yeti rate only: a row per tracked workspace, an overall bar, and a
     goal notch (null = off). */
  const [yetiByTeam, setYetiByTeam] = useState(false);
  const [yetiBar, setYetiBar] = useState(false);
  const [yetiGoal, setYetiGoal] = useState(null);
  const [yetiGoals, setYetiGoals] = useState({});
  /* Technicians' Revenue per Hour only: the card's own $/hr goal (null = the
     company's anchor goal, if it has one). */
  const [rphGoal, setRphGoal] = useState(null);
  /* Admins can point the card at any player; a player only ever sees their own
     numbers. On by default — switching it off is the deliberate act. */
  const [who, setWho] = useState(true);
  /* Width from the size pictures. null = whatever the content earns. */
  const [wStep, setWStep] = useState(null);

  /* The live dashed card on the board renders from this — every keystroke and
     pick lands on it, so building is never blind. Same object shape onConfirm
     sends, minus the trimming. */
  const chosenNow = rows.filter((r) => r.metricId);
  const draftNow = {
    title: name.trim(),
    metrics: chosenNow,
    bonus: sanitizeBonus(bonus),
    payoutCurve: payoutCurve && payoutCurve.on ? payoutCurve : null,
    pieceLayout: payoutCurve && payoutCurve.on ? (pieceLayout || null) : null,
    goalTarget: payoutCurve && payoutCurve.on && Number(goalTarget) > 0 ? Number(goalTarget) : null,
    workspaceIds,
    avgMode,
    metricPlan: hasMetricPlan(metricPlan) ? metricPlan : null,
    ...(chosenNow.some((r) => r.metricId === BEAT_YETI_RATE_ID) && { yetiByTeam, yetiBar, yetiGoal, yetiGoals }),
    ...(hasRphMetric(chosenNow) && rphGoal > 0 && { rphGoal }),
    look: look || null,
    show,
    showOn: show ? (showOn || chosenNow[0]?.metricId || null) : null,
    period,
    customRange: period === "custom" ? customRange : null,
    who,
  };
  const draftJson = JSON.stringify(draftNow);
  useEffect(() => {
    onDraft?.(JSON.parse(draftJson), wStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJson, wStep]);
  useEffect(() => () => { onEnd?.(); }, []); // panel closed any way at all → ghost goes

  const chosenRows = rows.filter((r) => r.metricId);
  const wsKey = (workspaceIds || []).map(String).filter(Boolean).sort().join(",");
  const scopedMetrics = useMemo(
    () => metricsForWorkspaces(metrics, wsKey ? wsKey.split(",") : [], selected?._id),
    [metrics, wsKey, selected?._id],
  );
  const metricOf = (id) => scopedMetrics.find((m) => m.id === id) || metrics.find((m) => m.id === id) || null;
  const bonusRule = sanitizeBonus(bonus);
  const looks = layoutsFor(chosenRows, bonusRule);
  // Ready = at least one metric, bonus (if on) complete, and payout curve (if on) has a bonus target.
  const ready = chosenRows.length > 0
    && (!bonus || !!bonusRule)
    && (!(payoutCurve && payoutCurve.on) || Number(goalTarget) > 0);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const pickMetric = (i, m) => setRow(i, { metricId: m.id, graph: defaultGraphFor(m) });
  const addRow = () => setRows((rs) => (rs.length >= MAX_METRICS ? rs : [...rs, { metricId: null, graph: "number" }]));
  const dropRow = (i) => setRows((rs) => (rs.length <= 1 ? [{ metricId: null, graph: "number" }] : rs.filter((_, x) => x !== i)));

  // Workspace scope changed → drop metrics that don't belong anymore.
  const scopedIdsKey = scopedMetrics.map((m) => m.id).join("|");
  useEffect(() => {
    const allowed = new Set(scopedMetrics.map((m) => m.id));
    setRows((rs) => {
      const next = rs.map((r) => (r.metricId && !allowed.has(r.metricId) ? { ...r, metricId: null, graph: "number" } : r));
      return next.some((r, i) => r.metricId !== rs[i].metricId) ? next : rs;
    });
    if (bonus && bonus.ofMetric && !allowed.has(bonus.ofMetric)) setBonus((b) => (b ? { ...b, ofMetric: null } : b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedIdsKey]);

  const confirm = () => {
    if (!ready) return;
    /* Empty workspace pick used to save no scope — the card then fell back to
       the host/first workspace, which often wasn't the team those metrics live
       on → "Not available yet". Default to the workspace you're viewing. */
    const scopedWs = (workspaceIds || []).filter(Boolean).map(String);
    let ws = scopedWs;
    if (!ws.length && selected?._id) ws = [String(selected._id)];
    /* One team = separate metric tiles. Never persist a cross-ws pairing. */
    const multi = ws.length >= 2;
    const cleanRows = chosenRows
      .filter((r) => r && r.metricId && String(r.metricId) !== "paired")
      .map((r) => ({ metricId: r.metricId, graph: r.graph || "number", color: r.color || null }));
    onConfirm({
      title: name.trim(),
      metrics: cleanRows,
      bonus: bonusRule,
      payoutCurve: payoutCurve && payoutCurve.on ? payoutCurve : null,
      pieceLayout: payoutCurve && payoutCurve.on ? (pieceLayout || null) : null,
      goalTarget: payoutCurve && payoutCurve.on && Number(goalTarget) > 0 ? Number(goalTarget) : null,
      workspaceIds: ws,
      avgMode,
      metricPlan: multi && hasMetricPlan(metricPlan) ? metricPlan : null,
      ...(cleanRows.some((r) => r.metricId === BEAT_YETI_RATE_ID) && { yetiByTeam, yetiBar, yetiGoal, yetiGoals }),
      ...(hasRphMetric(cleanRows) && rphGoal > 0 && { rphGoal }),
      look: look || (looks[0] && looks[0].key) || null,
      show,
      showOn: show ? (showOn || cleanRows[0]?.metricId || null) : null,
      period,
      customRange: period === "custom" ? customRange : null,
      who,
    });
  };

  const available = (i) => scopedMetrics.filter((m) => !rows.some((r, x) => x !== i && r.metricId === m.id));

  return (
    <>
      <div className="gmbw-panel-h">
        <div className="gmbw-panel-htxt">
          <button type="button" className="gmbw-backlink" onClick={onCancel}>
            <Icon d={IcBack} size={13} /> All components
          </button>
          <span className="gmbw-panel-title">Create a component</span>
          <span className="gmbw-panel-sub">Set it up, then choose how it looks</span>
        </div>
        <button type="button" className="gmbw-panel-x" onClick={onClose} aria-label="Close">
          <Icon d={IcX} size={15} />
        </button>
      </div>

      <div className="gmbw-etabs">
        <button type="button" className={`gmbw-etab${tab === "setup" ? " on" : ""}`} onClick={() => setTab("setup")}>
          Setup
        </button>
        <button
          type="button"
          className={`gmbw-etab${tab === "look" ? " on" : ""}${ready ? " ready" : " off"}`}
          onClick={() => ready && setTab("look")}
          disabled={!ready}
        >
          Visualisation{ready && <i className="gmbw-cue" />}
        </button>
      </div>

      <div className="gmbw-create">
        {tab === "setup" && (
          <>
            <div className="gmbw-field">
              <label className="gmbw-flabel" htmlFor="gmbw-cw-name">Component name</label>
              <input
                id="gmbw-cw-name"
                className="gmbw-input"
                type="text"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Self-generated"
              />
              <span className="gmbw-fhint">Leave blank to use the metric’s name.</span>
            </div>

            {/* Workspaces first — the metric list below only offers KPIs those
                workspaces actually track (not the whole company roster). */}
            <div className="gmbw-field">
              <ScopeWindow
                draft={{ workspaceIds, avgMode }}
                onChange={(d) => { setWorkspaceIds(d.workspaceIds || []); setAvgMode(d.avgMode || "avg"); }}
                fields={["workspaces", "show"]}
              />
            </div>

            {usesMetricPairing(workspaceIds, chosenRows) ? (
              <>
                <MetricPlanEditor
                  draft={{ workspaceIds, avgMode, metricPlan }}
                  metricCatalog={metrics}
                  onChange={(d) => {
                    setMetricPlan(d.metricPlan || null);
                    setAvgMode(d.avgMode || avgMode);
                    if (hasMetricPlan(d.metricPlan)) {
                      const cols = (d.metricPlan.cols || []).filter((c) => c && c.show !== false);
                      setRows(cols.length
                        ? cols.map((c) => ({ metricId: c.key, graph: "number" }))
                        : [{ metricId: null, graph: "number" }]);
                    }
                  }}
                />
                <p className="gmbw-fhint" style={{ marginTop: 4 }}>
                  Switch to <b>Custom</b> and pick one metric per team — those become <b>one</b> number on the card.
                </p>
              </>
            ) : (
            <div className="gmbw-field">
              <label className="gmbw-flabel">Metrics</label>
              {rows.map((r, i) => {
                const m = metricOf(r.metricId);
                return (
                  /* eslint-disable-next-line react/no-array-index-key */
                  <div className="gmbw-mrow" key={`row${i}`}>
                    <div className="gmbw-mrow-top">
                      <MetricPicker metrics={available(i)} value={r.metricId} onPick={(mm) => pickMetric(i, mm)} />
                      {rows.length > 1 && (
                        <button type="button" className="gmbw-mrow-x" onClick={() => dropRow(i)} aria-label="Remove this metric">
                          <Icon d={IcX} size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {rows.length < MAX_METRICS ? (
                <button type="button" className="gmbw-addrow" onClick={addRow}>
                  <Icon d={IcPlus} size={13} /> Add another metric
                </button>
              ) : (
                <div className="gmbw-addrow full">{MAX_METRICS} metrics — that’s the max</div>
              )}
              {!scopedMetrics.length && (
                <p className="gmbw-fhint" style={{ marginTop: 8 }}>
                  No metrics on {workspaceIds.length ? "these workspaces" : "this workspace"} yet.
                </p>
              )}
            </div>
            )}

            {chosenRows.some((r) => r.metricId === BEAT_YETI_RATE_ID) && (
              <YetiOptionsField
                value={{ yetiByTeam, yetiBar, yetiGoal, yetiGoals }}
                workspaces={trackedWorkspaces(
                  (workspaceIds || []).filter(Boolean).length ? workspaceIds : [selected?._id],
                  workspaces,
                )}
                onChange={(patch) => {
                  if ("yetiGoals" in patch) setYetiGoals(patch.yetiGoals || {});
                  if ("yetiByTeam" in patch) setYetiByTeam(!!patch.yetiByTeam);
                  if ("yetiBar" in patch) setYetiBar(!!patch.yetiBar);
                  if ("yetiGoal" in patch) setYetiGoal(patch.yetiGoal);
                }}
              />
            )}

            {hasRphMetric(chosenRows) && <RphGoalField value={rphGoal} onChange={setRphGoal} />}

            <div className="gmbw-field">
              <BonusField
                value={bonus}
                onChange={setBonus}
                chosen={chosenRows.map((r) => metricOf(r.metricId))}
                catalog={scopedMetrics}
                metricOf={metricOf}
                labelClassName="gmbw-flabel"
                showHint
              />
            </div>

            {/* One container, because these are the same question asked twice:
                which slice of the data the card is about. "Who it shows" as its
                own titled box read as a second, unrelated setting. */}
            <div className="gmbw-field">
              <label className="gmbw-flabel">Filters</label>
              <p className="gmbw-fhint" style={{ margin: "0 0 9px" }}>
                Which numbers this card is about.
              </p>
              <PeriodField
                period={period}
                customRange={customRange}
                onChange={({ period: p, customRange: cr }) => { setPeriod(p); setCustomRange(cr); }}
              />
              <span className="gmbw-fhint" style={{ margin: "6px 0 10px" }}>
                Match the dashboard and the filter at the top of the board changes this card too.
              </span>
              <button
                type="button"
                className={`gmbw-whotog${who ? " on" : ""}`}
                onClick={() => setWho(!who)}
                aria-pressed={who}
              >
                <span className="gmbw-whotog-t">
                  <b>Let admins switch player</b>
                  <span>Admins get a name dropdown on the card. Players always see their own numbers.</span>
                </span>
                <span className="gmbw-whotog-sw"><i /></span>
              </button>
            </div>

            <div className="gmbw-field">
              <PayoutCurveAdvanced
                value={payoutCurve}
                onChange={setPayoutCurve}
                layout={pieceLayout}
                onLayout={setPieceLayout}
                metricOptions={scopedMetrics.map((m) => {
                  const base = m.label || m.name || String(m.id);
                  const tag = metricWorkspaceTag(m, workspaces);
                  return { value: m.id, label: tag ? `${base} — ${tag}` : base, metricName: base };
                })}
                multiWs={(workspaceIds || []).filter(Boolean).length >= 2}
              />
              {payoutCurve && payoutCurve.on && (
                <div style={{ marginTop: 12 }}>
                  <label className="gmbw-blabel" htmlFor="gmbw-create-goal-target">Bonus target for this card</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="gmbw-pc-dollar">$</span>
                    <input
                      id="gmbw-create-goal-target"
                      className="gmbw-input"
                      type="number"
                      min="1"
                      step="1000"
                      required
                      placeholder="e.g. 1000000"
                      value={goalTarget != null && goalTarget !== "" ? goalTarget : ""}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setGoalTarget(Number.isFinite(n) && n > 0 ? n : null);
                      }}
                    />
                  </div>
                  <p className="gmbw-fhint" style={{ marginTop: 6 }}>
                    Required for the payout curve. Dollar labels under each % tier are this target × that %.
                  </p>
                </div>
              )}
            </div>

            {ready && (
              <div className="gmbw-ready">
                <span className="gmbw-ready-ic">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
                </span>
                <span>
                  <b>Ready</b>
                  Tap <b>Visualisation</b> above to choose how it looks.
                </span>
              </div>
            )}
          </>
        )}

        {tab === "look" && (
          <>
          {/* Addressed by metricId, never by position. This list is
              `chosenRows` (metrics actually picked) while `setRow(i)` indexes
              the FULL rows array including the empty one waiting at the bottom
              — so picking a graph on the second metric wrote it to the first,
              or to a blank row, and nothing appeared to happen. */}
          {chosenRows.map((r, i) => {
            const mm = metricOf(r.metricId);
            const at = rows.findIndex((x) => x.metricId === r.metricId);
            return (
              <div className="gmbw-field" key={`look${r.metricId}`}>
                <label className="gmbw-flabel">{mm?.label || mm?.title || "Metric"}</label>
                <p className="gmbw-fhint" style={{ margin: "0 0 8px" }}>How this one is drawn.</p>
                <div className="gmbw-mrow">
                  <div className="gmbw-mrow-g">
                    <span>Graph</span>
                    <GraphPicker value={r.graph} onChange={(g) => setRow(at, { graph: g })} />
                    <span>Colour</span>
                    <ColourPicker
                      value={r.color}
                      fallback={METRIC_COLORS[i % METRIC_COLORS.length]}
                      onChange={(c) => setRow(at, { color: c })}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <div className="gmbw-field gmbw-showbox">
            <label className="gmbw-flabel">Show more under the numbers</label>
            <p className="gmbw-fhint" style={{ margin: "0 0 9px" }}>
              Pick one, or nothing. Two would make the card taller than the one beside it.
            </p>
            <ShowPicker
              value={show}
              on={showOn}
              rows={chosenRows}
              metricOf={metricOf}
              onChange={setShow}
              onPickMetric={setShowOn}
            />
          </div>

          <div className="gmbw-field">
            <label className="gmbw-flabel">Suggested layouts</label>
            <p className="gmbw-fhint" style={{ margin: "0 0 8px" }}>
              For {chosenRows.length} metric{chosenRows.length === 1 ? "" : "s"}{bonusRule ? " with a bonus" : ""}.
            </p>
            {looks.map((L, i) => (
              <button
                key={L.key}
                type="button"
                className={`gmbw-look${(look || looks[0].key) === L.key ? " on" : ""}`}
                onClick={() => setLook(L.key)}
              >
                {i === 0 && <span className="gmbw-look-rec">Recommended</span>}
                <LayoutThumb kind={L.key} bonus={!!bonusRule} sample={thumbSample({ name, rows: chosenRows, metricOf, bonus: bonusRule })} />
                <span className="gmbw-look-t">
                  <b>{L.label}</b>
                  <span>{L.blurb}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="gmbw-field">
            <label className="gmbw-flabel">Widget size</label>
            <p className="gmbw-fhint" style={{ margin: "0 0 8px" }}>
              How much of the row it takes. You can still drag its corner later.
            </p>
            <SizePicker
              value={wStep || customWidthFor(draftNow)}
              floor={customWidthFor(draftNow)}
              sample={thumbSample({ name, rows: chosenNow, metricOf, bonus: draftNow.bonus })}
              onPick={setWStep}
            />
          </div>
          </>
        )}
      </div>

      <div className="gmbw-edit-ft">
        <button type="button" className="gmbw-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="gmbw-btn primary" onClick={confirm} disabled={!ready}>
          Add to board
        </button>
      </div>
    </>
  );
};

const CustomizeDrawer = ({
  open,
  onClose,
  onAdd,
  onRemove,
  onOpenTemplates,
  onDragWidget,
  boardIds = [],
  brandVars,
  // editing
  editing,          // { id, draft, pending } | null
  onConfigure,      // (id) => void — set up before it lands
  onEdit,           // (id) => void — edit something already placed
  onEditChange,     // (draft) => void
  onEditSize,       // (sizeKey) => void
  money,            // { program, metrics, workspaceName, scoredOnLabel, ytd } | null
  onMoneyChange,    // (program) => void
  onEditCancel,
  onEditSave,
  metricCatalog = [], // [{ id, label, kind, target, group }] — for "Build your own"
  boardWidgets = [], // the board's own blocks — Revenue, Expenses & Labor names a Payroll vs Revenue card among them
  onCreateCustom,   // ({ title, metricId, graph }) => void
  onCreateStart,    // () => void — a live dashed card is born on the board
  onCreateDraft,    // (draft, w) => void — the form's current state, rendered on it
  onCreateEnd,      // () => void — creation abandoned; the ghost goes
  onWidth,          // (id, step) => void — size pictures resize the card live
  editBlockW,       // current grid width of the card being edited
  // which half of the panel the nav tab is on, and the board to compare
  // templates against so the one in use can say so
  tab = "components",
  onTab,
  onApplyTemplate,  // (name, layout) => void — routed through the confirm
  currentLayout = [],
}) => {
  const [query, setQuery] = useState("");
  // Local: the "Build your own" create flow replaces the library while open.
  const [creating, setCreating] = useState(false);
  // Filter by what the component is FOR (Sales, Performance, …). Whether a
  // component is already on the board is shown as an "Added" badge on the card,
  // not as a filter.
  const [cat, setCat] = useState("All");

  const q = query.trim().toLowerCase();

  // Every component, in a deliberate order (what people reach for first).
  const allWidgets = useMemo(() => {
    const rank = (w) => {
      const i = ORDER.indexOf(w.group);
      return i === -1 ? ORDER.length : i;
    };
    return WIDGETS.slice().sort((a, b) => rank(a) - rank(b));
  }, []);

  // The blank "Build your own" card is not listed here — it has its own primary
  // button at the top of the panel, so it never sits at the bottom of the list.
  const listWidgets = useMemo(() => allWidgets.filter((w) => !w.blank && !w.analyticsOnly), [allWidgets]);

  const counts = useMemo(() => {
    const out = {};
    listWidgets.forEach((w) => {
      out[w.group] = (out[w.group] || 0) + 1;
    });
    return out;
  }, [listWidgets]);

  const shown = listWidgets.filter((w) => {
    if (cat !== "All" && w.group !== cat) return false;
    return !q || w.title.toLowerCase().includes(q) || (w.blurb || "").toLowerCase().includes(q);
  });

  // The create flow is a peer of editing; never show both. It also clears when
  // the drawer closes so it never reappears on the next open.
  useEffect(() => {
    if (!open || editing) setCreating(false);
  }, [open, editing]);

  if (!open) return null;

  const editingDef = editing ? widgetById(editing.id) : null;

  const library = (
    <>
      <div className="gmbw-panel-h">
        <div className="gmbw-panel-htxt">
          <span className="gmbw-panel-title">Components</span>
          <span className="gmbw-panel-sub">Drag any of these onto your board</span>
        </div>
        <button type="button" className="gmbw-panel-x" onClick={onClose} aria-label="Close">
          <Icon d={IcX} size={15} />
        </button>
      </div>

      <div className="gmbw-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search components"
          aria-label="Search components"
        />
      </div>

      <div className="gmbw-libchips">
        <button
          type="button"
          className={`gmbw-libchip${cat === "All" ? " on" : ""}`}
          onClick={() => setCat("All")}
        >
          All <span>{listWidgets.length}</span>
        </button>
        {ORDER.filter((g) => counts[g]).map((g) => (
          <button
            key={g}
            type="button"
            className={`gmbw-libchip${cat === g ? " on" : ""}`}
            onClick={() => setCat(g)}
          >
            {g} <span>{counts[g]}</span>
          </button>
        ))}
      </div>

      <div className="gmbw-lib-list">
        {/* Build-your-own is the first CHOICE in the list, not a banner above it.
            Sitting above the search with a filled background it read as a header
            — a title for the panel rather than something you could pick. Here,
            outlined and directly on top of the first component, it reads as
            "…or make one yourself". */}
        <button type="button" className="gmbw-byo-cta" onClick={() => { setCreating(true); onCreateStart?.(); }}>
          <span className="gmbw-byo-plus"><Icon d={IcPlus} size={16} /></span>
          <span className="gmbw-byo-txt">
            <b>Create a component</b>
            <span>Pick a metric, choose how it&rsquo;s drawn</span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
        </button>
        {shown.map((w) => (
          <LibItem
            key={w.id}
            w={w}
            /* Count copies by BASE id: a board holding earnings#2 and #3 still
               shows the Anticipated earnings row as added, ×2. */
            added={boardIds.some((id) => baseId(id) === w.id)}
            copies={boardIds.filter((id) => baseId(id) === w.id).length}
            onAdd={onAdd}
            onConfigure={onConfigure}
            onEdit={onEdit}
            onRemove={onRemove}
            onDragWidget={onDragWidget}
            onCreate={() => { setCreating(true); onCreateStart?.(); }}
          />
        ))}
        {!shown.length && (
          <div className="gmbw-lib-empty">{emptyMessage(q, query)}</div>
        )}
      </div>

      {onOpenTemplates && (
        <div className="gmbw-tplrow">
          <span className="gmbw-tplrow-ic"><Icon d={IcGrid} size={15} /></span>
          <span className="gmbw-tplrow-t">
            <b>Start from a template</b>
            <span>Replaces everything on the board</span>
          </span>
          <button type="button" className="gmbw-btn sm" onClick={onOpenTemplates}>Browse</button>
        </div>
      )}
    </>
  );

  const templates = (
    <>
      <div className="gmbw-panel-h">
        <div className="gmbw-panel-htxt">
          <span className="gmbw-panel-title">Templates</span>
          <span className="gmbw-panel-sub">Swap the whole board in one move</span>
        </div>
        <button type="button" className="gmbw-panel-x" onClick={onClose} aria-label="Close">
          <Icon d={IcX} size={15} />
        </button>
      </div>
      <TemplateList
        current={currentLayout}
        onApply={(name, layout) => { onApplyTemplate?.(name, layout); onTab?.("components"); }}
      />
    </>
  );

  let content = tab === "templates" ? templates : library;
  if (editing && editingDef) {
    content = (
      <Editor
        def={editingDef}
        draft={editing.draft}
        pending={editing.pending}
        size={editing.size}
        blockW={editBlockW}
        savedAt={editing.savedAt}
        money={money}
        metricCatalog={metricCatalog}
        boardWidgets={boardWidgets}
        onChange={onEditChange}
        onSize={onEditSize}
        onWidth={onWidth}
        onMoney={onMoneyChange}
        onCancel={onEditCancel}
        onSave={onEditSave}
        onClose={onClose}
      />
    );
  } else if (creating) {
    content = (
      <CreatePanel
        metrics={metricCatalog}
        onCancel={() => { setCreating(false); onCreateEnd?.(); }}
        onConfirm={(s) => { onCreateCustom?.(s); setCreating(false); }}
        onClose={onClose}
        onDraft={onCreateDraft}
        onEnd={onCreateEnd}
      />
    );
  }

  return createPortal(
    <aside className="gmbw-panel" style={brandVars} aria-label={editing ? "Edit component" : "Add a widget"}>
      {content}
    </aside>,
    document.body,
  );
};

export default CustomizeDrawer;
